import { client } from "../api/client.js";
import PLANS from "../data/plans.json";
import { renderPageHeader } from "../ui/pageHeader.js";
import { confirmModal } from "../ui/confirmModal.js";
import { promptModal } from "../ui/promptModal.js";
import { toast } from "../ui/toast.js";
import { openDocPicker } from "../ui/docPicker.js";
import { isComingSoon } from "../data/comingSoon.js";
import { startCoachmarkTour } from "../ui/coachmarkTour.js";
// Connectors + Agent Inbox no longer mount inside Settings — the Agent
// Inbox tab here is a primer that links out to /#agent-inbox (full
// review UI) and /#help?tab=mcp (setup walkthrough + copyable prompts).

/**
 * Section-level help copy. Keyed by the section's HTML id (matches the
 * data-section attribute on the nav buttons). Body lines stay short — long
 * explanations belong in the Help page (`#help?...`) and are reachable via
 * a "Learn more" link on each banner.
 */
const SECTION_HELP = {
  'section-integrations': {
    title: 'Integrations',
    body: 'Hook your social accounts (Facebook, Instagram, X, LinkedIn, TikTok, YouTube, Pinterest, Reddit, WordPress) to Heist so it can publish for you. Each connection is a one-time OAuth handshake; tokens stay encrypted at rest. Instagram requires a linked Facebook Business Page (Meta\'s rule, not ours).',
    helpLink: '#help?tab=connect',
  },
  'section-ai-settings': {
    title: 'AI Settings',
    body: 'Paste your own OpenAI / Anthropic / Google API key so Heist\'s built-in writer uses your model. Pay your provider directly, skip Heist\'s shared token caps. This is for Heist\'s writer — to use Heist *from inside* Claude.ai or ChatGPT instead, see the AI chat connector.',
    helpLink: '#help?tab=connect&connect=connect-anthropic',
  },
  'section-agent-inbox': {
    title: 'Agent Inbox',
    body: 'Pro+ feature. Drafts written by your AI chat (Claude.ai, ChatGPT) queue here for review. Approve, edit, or toss before they hit the calendar. New here? The 30-second connector setup lives under Learn more →.',
    helpLink: '#help?tab=mcp',
  },
  'schedule-rules-section': {
    title: 'Schedule rules',
    body: 'Tell Heist when you want to post on each platform — e.g. "Tue/Thu 9am for Instagram, never weekends." Auto-fill uses these rules to slot drafts into the calendar.',
    helpLink: '#help?tab=schedule',
  },
  'section-brands': {
    title: 'Brands',
    body: 'Each brand has its own Brain, library, calendar, and integrations. Switch between them here, or add a new brand if you manage multiple businesses.',
    helpLink: '#help?tab=brands',
  },
  'section-account': {
    title: 'Profile',
    body: 'Your name, photo, email, and password. These are personal — they don\'t change anything brand-related.',
    helpLink: null,
  },
  'section-billing': {
    title: 'Billing',
    body: 'Plan, payment method, and invoice history. Upgrades take effect immediately; downgrades apply at the next renewal.',
    helpLink: '#help?tab=billing',
  },
  'section-team-management': {
    title: 'Team',
    body: 'Invite teammates to your workspace and assign roles (Owner, Admin, Editor, Viewer). Available on Pro and above.',
    helpLink: '#help?tab=team',
  },
  'section-approvals': {
    title: 'Approvals',
    body: 'Review and approve drafts your team submitted before they go live. Pending items also show up in the bell drawer.',
    helpLink: null,
  },
  'section-activity': {
    title: 'Activity',
    body: 'A timeline of who did what in your workspace — created, edited, scheduled, published, deleted.',
    helpLink: null,
  },
  'section-team': {
    title: 'Data & Privacy',
    body: 'Export your data, delete your account, and review what Heist stores. We never train models on your content.',
    helpLink: '#help?tab=privacy',
  },
};

/**
 * Render a small ⓘ help-chip aligned to the right of a section title.
 * Click toggles a popover with the section's help text + an optional
 * "Learn more →" link. Pure HTML — popover positioning is handled by CSS
 * + a JS click handler in attachListeners that uses state.helpChipOpen.
 */
function renderHelpChip(sectionId) {
  const h = SECTION_HELP[sectionId];
  if (!h) return '';
  return `<button class="h-help-chip" type="button" data-help-chip="${sectionId}" aria-label="${escapeAttr(h.title)} — help" data-testid="settings-help-${sectionId}">i</button>`;
}

function renderHelpPopover(sectionId, anchorId) {
  const h = SECTION_HELP[sectionId];
  if (!h) return '';
  const learn = h.helpLink
    ? `<a class="h-help-popover__link" href="${h.helpLink}">Learn more →</a>`
    : '';
  return `
    <div class="h-help-popover" data-help-popover="${sectionId}" role="tooltip" id="${anchorId}">
      <div style="font-weight:700;margin-bottom:4px;">${h.title}</div>
      <div style="color:var(--text-2);">${h.body}</div>
      ${learn}
    </div>
  `;
}

function escapeAttr(s) {
  return String(s == null ? '' : s).replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Render a unified section header: eyebrow (group label) + title +
 * help-chip + 1-line lede. Sections can pass `extraTitleHtml` to slot
 * affordances next to the title (e.g. brand switcher).
 */
/**
 * AI chat platform tiles — same set as the setup wizard's platform picker.
 * Each tile opens the chat in a new tab; the user signs in there and uses
 * the Heist Connector they set up under Connections → Connectors. Drafts
 * the AI writes flow back to the Agent Inbox here.
 */
const AGENT_INBOX_PLATFORMS = [
  { id: 'claude',   name: 'Claude.ai',         sub: 'Browser sign-in — fastest path',    url: 'https://claude.ai/' },
  { id: 'chatgpt',  name: 'ChatGPT',            sub: 'Browser sign-in — Pro / Business',  url: 'https://chatgpt.com/' },
  { id: 'cursor',   name: 'Cursor',             sub: 'Desktop app — needs an API key',    url: 'https://cursor.com/' },
  { id: 'windsurf', name: 'Windsurf',           sub: 'Desktop app — needs an API key',    url: 'https://windsurf.com/' },
  { id: 'copilot',  name: 'Copilot (VS Code)',  sub: 'VS Code — needs an API key',         url: 'https://code.visualstudio.com/' },
];
// Real platform icons via Google's favicon CDN — same source the wizard
// uses, so the two pickers stay visually consistent.
function _platformIcon(domain, alt) {
  return `<img src="https://www.google.com/s2/favicons?domain=${domain}&sz=64" alt="${alt}" width="28" height="28" loading="lazy" />`;
}
const AGENT_INBOX_PLATFORM_ICONS = {
  claude:   _platformIcon('claude.ai', 'Claude'),
  chatgpt:  _platformIcon('chatgpt.com', 'ChatGPT'),
  cursor:   _platformIcon('cursor.com', 'Cursor'),
  windsurf: _platformIcon('windsurf.com', 'Windsurf'),
  copilot:  _platformIcon('code.visualstudio.com', 'VS Code'),
};

function renderAgentInboxPrimer() {
  return `
    <div class="agent-inbox-primer">
      <div class="agent-inbox-primer__intro">
        <div class="h-eyebrow">How it works</div>
        <p class="h-lede" style="margin-top:6px;">
          Connect Heist to an AI chat (one-time setup — instructions + a copyable starter prompt live in the Help center). Then ask that AI to write posts, captions, or campaign ideas the way you normally would. Every draft it writes flows back here for you to review, edit, approve, or toss before it hits your Library.
        </p>
      </div>

      <div class="h-eyebrow" style="margin-top:24px;margin-bottom:10px;">Where drafts come from</div>
      <div class="agent-inbox-platforms">
        ${AGENT_INBOX_PLATFORMS.map(p => `
          <a class="agent-inbox-platform" href="${p.url}" target="_blank" rel="noopener noreferrer">
            <span class="agent-inbox-platform__icon">${AGENT_INBOX_PLATFORM_ICONS[p.id] || ''}</span>
            <span class="agent-inbox-platform__body">
              <span class="agent-inbox-platform__name">${p.name}</span>
              <span class="agent-inbox-platform__sub">${p.sub}</span>
            </span>
          </a>
        `).join('')}
      </div>

      <div class="h-callout h-callout--info" style="margin-top:20px;">
        <div class="h-callout__icon">i</div>
        <div>
          <strong>First time?</strong> Walk through the setup + copy a starter prompt in the Help center.
          <button type="button" data-action="agent-inbox-open-help" style="background:none;border:none;padding:0;color:var(--primary);font-weight:600;cursor:pointer;font-size:inherit;font-family:inherit;margin-left:6px;">Plan content from your favorite AI chat →</button>
        </div>
      </div>

      <div class="h-save-row" style="justify-content:flex-end;margin-top:24px;">
        <button class="h-btn h-btn--primary" data-action="agent-inbox-open" type="button">
          Open Agent Inbox →
        </button>
      </div>
    </div>
  `;
}

function renderPaneHead(sectionId, { eyebrow, title, lede, extraTitleHtml = '' }) {
  return `
    <div class="h-pane-head">
      ${eyebrow ? `<div class="h-eyebrow">${eyebrow}</div>` : ''}
      <h2 class="h-title">
        <span>${title}</span>
        ${extraTitleHtml}
        ${renderHelpChip(sectionId)}
      </h2>
      ${lede ? `<p class="h-lede">${lede}</p>` : ''}
    </div>
  `;
}

// MCP / Agent Inbox gate — Pro tier and above.
// Kept in sync with server/middleware/tier.js MCP_ALLOWED_TIERS.
// Trial users get full Pro access during their evaluation window.
function isProTier() {
  const u = window.__auth__ || {};
  if (['admin', 'angel_admin', 'god_admin'].includes(u.role)) return true;
  return ['trial', 'pro', 'business', 'founder', 'agency'].includes(u.tier);
}

export class SettingsPage {
  constructor() {
    this.state = {
      loading: true,
      error: null,
      itemsByProvider: {}, // { meta: {...}, x: {...}, linkedin: {...} }
      connectingProvider: null, // 'meta', 'x', 'linkedin' or null
      // Phase 36B: edit hints keyed by provider — { non_secret: {...}, secret_set: {...} }.
      // Populated on Edit click before we render the form, so inputs can be pre-filled
      // with non-secret values and secret inputs can show "Currently set".
      editHints: {},
      testingProvider: null,
      message: null, // { type: 'success'|'error', text: string }

      // AI Settings
      aiSettings: null,
      aiLoading: true,
      aiError: null,
      aiMessage: null,
      aiDraftMode: null, // temporary mode selection before save
      aiDraftKey: "",

      // Brain-write approval mode (Phase D1) — 'review' queues MCP agent
      // writes in the Agent Inbox; 'instant' applies them immediately.
      brainWriteMode: 'review',
      brainWriteSavingStatus: '',

      // Account & Profile
      accountData: null,       // populated from GET /api/auth/me
      accountSaving: false,
      accountMsg: null,        // { type: 'success'|'error', text: string }
      passwordSaving: false,
      passwordMsg: null,
      brandSaveMsg: null,
      activeSection: 'section-integrations',
      expandedGroups: new Set(['connections']),
      // Which section's ⓘ help-chip popover is open (null = none).
      helpChipOpen: null,
      // Mobile nav sheet open state (settings page only — collapses on item select).
      mobileNavOpen: false,

      // Billing
      billingData: null,
      billingCycle: 'monthly',
      billingLoading: false,

      // Brands
      brands: [],
      brandsLoading: false,

      // Workspace / Team
      workspace: null,
      workspaceLoading: false,
      workspaceMembers: [],
      pendingInvites: [],
      myWorkspaceRole: null,
      agencyBrain: null,

      // Approvals
      approvals: [],
      approvalsLoading: false,
      approvalFilter: 'pending',
      approvalSelectedIds: new Set(), // Phase 32+ — bulk approve/reject UX

      // Brand Shares
      brandShares: [],

      // Activity Log
      activityEntries: [],
      activityLoading: false,
      activityBrandFilter: '',
      activityActionFilter: '',
      activityOffset: 0,

      // Phase 31.5B — Website/Blog tracked sites
      trackedSites: [],
      siteAddInputs: { website: '', blog: '' },
      siteDetectState: null, // { platform, url, loading, result, error } | null
      siteAddedDetails: null, // { site, pixel_snippet } after successful add
    };
    this.el = null;
    this.scheduleRules = [];
  }

  async mount(el) {
    this.el = el;
    this.renderSkeleton();
    this.attachListeners(); // S1.2.1b: Attach ONCE (delegation)
    await Promise.all([this.fetchIntegrations(), this.fetchAiSettings(), this.fetchScheduleRules(), this.fetchAccountData(), this.fetchBillingStatus(), this.loadBrands(), this.loadWorkspace(), this.fetchBrainWriteMode()]);

    // Check Google Docs / Notion connection status
    try {
      const [gRes, nRes] = await Promise.all([
        client.googleDocsStatus().catch(() => ({ connected: false })),
        client.notionStatus().catch(() => ({ connected: false }))
      ]);
      this._googleConnected = !!(gRes && gRes.connected);
      this._notionConnected = !!(nRes && nRes.connected);
    } catch { this._googleConnected = false; this._notionConnected = false; }

    // Load server key policy for admin users
    if (['admin', 'angel_admin', 'god_admin'].includes(window.__auth__?.role)) {
        try {
            const policyRes = await client.getServerKeyPolicy();
            this.state.serverKeyPolicy = policyRes.policy || 'none';
            this.state.serverKeys = policyRes.serverKeys || {};
        } catch { /* non-fatal */ }
    }

    // Handle OAuth redirect params (e.g. ?oauth=meta_connected or ?error=meta_auth_denied)
    this._handleOAuthRedirect();

    // Handle ?section=... deep link (e.g. from notifications)
    const hashParams = new URLSearchParams((window.location.hash.split('?')[1]) || '');
    const sectionParam = hashParams.get('section');
    if (sectionParam) {
      const groupMap = { 'section-integrations':'connections','section-ai-settings':'connections','schedule-rules-section':'content','section-brands':'content','section-account':'account','section-billing':'account','section-team-management':'account','section-approvals':'account','section-activity':'account','section-team':'privacy' };
      this.state.expandedGroups.add(groupMap[sectionParam] || 'connections');
      this.setState({ activeSection: sectionParam });
      if (sectionParam === 'section-approvals') this.loadApprovals();
      if (sectionParam === 'section-activity') this.loadActivity();
    }

    // Handle seat purchase success redirect
    if (window.location.hash.includes('seats=success')) {
      window.location.hash = '#settings?section=section-team-management';
      this.loadWorkspace();
      setTimeout(() => {
        const toast = document.createElement('div');
        toast.className = 'toast-banner success';
        toast.innerHTML = '<span>Seats added successfully!</span>';
        this.el?.prepend(toast);
        setTimeout(() => toast.remove(), 5000);
      }, 500);
    }
  }

  _handleOAuthRedirect() {
    var params = new URLSearchParams(window.location.search || window.location.hash.split('?')[1] || '');
    var oauthSuccess = params.get('oauth');
    var oauthError = params.get('error');

    if (oauthSuccess) {
      var provider = oauthSuccess.replace('_connected', '');
      var label = provider === 'meta' ? 'Meta' : provider === 'linkedin' ? 'LinkedIn' : provider === 'tiktok' ? 'TikTok' : provider === 'youtube' ? 'YouTube' : provider === 'google_docs' ? 'Google Docs' : provider === 'wordpress' ? 'WordPress.com' : provider === 'pinterest' ? 'Pinterest' : provider === 'reddit' ? 'Reddit' : provider;
      this.setState({ message: { type: 'success', text: label + ' connected successfully via OAuth.' } });
      // Re-fetch integration statuses so the card updates immediately
      this.fetchIntegrations();
      if (provider === 'google_docs' || provider === 'notion') {
        client.googleDocsStatus().then(r => { this._googleConnected = !!(r && r.connected); this.render(); }).catch(() => {});
        client.notionStatus().then(r => { this._notionConnected = !!(r && r.connected); this.render(); }).catch(() => {});
      }
      // Clean the URL
      history.replaceState(null, '', '/#settings');
    } else if (oauthError) {
      var errorMessages = {
        'youtube_no_channel': 'No YouTube channel found on this Google account. Please create a YouTube channel first at youtube.com, then try connecting again.',
        'google_auth_failed': 'Google Docs connection failed. Please try again.',
        'youtube_auth_failed': 'YouTube connection failed. Please try again.',
        'youtube_auth_denied': 'YouTube authorization was denied.',
        'youtube_no_refresh_token': 'YouTube did not provide a refresh token. Try disconnecting and reconnecting.',
        'wordpress_auth_denied': 'WordPress.com authorization was denied.',
        'wordpress_auth_failed': 'WordPress.com connection failed. Please try again.',
        'wordpress_token_failed': 'WordPress.com token exchange failed. Please try again.',
        'meta_auth_denied': 'Meta authorization was denied.',
        'meta_auth_missing_params': 'Meta did not return the expected response. Please try again.',
        'meta_auth_failed': 'Meta connection failed. Please try again.',
        'meta_no_pages': 'No Facebook Pages found on your account. Meta requires a Facebook Page to post content. Create a Page at facebook.com/pages/create, then try connecting again. During the "Heist" permission dialog, make sure you select your Page and grant all requested permissions.'
      };
      var msg = errorMessages[oauthError] || oauthError.replace(/_/g, ' ');
      this.setState({ message: { type: 'error', text: msg } });
      history.replaceState(null, '', '/#settings');
    }
  }

  async unmount() {
    this.el = null;
  }

  async fetchIntegrations() {
    try {
      this.setState({ loading: true, error: null });
      const res = await client.getIntegrations(); // S1.1 endpoint
      if (res.ok) {
        // Map list to object for easy lookup
        const map = res.items.reduce((acc, item) => {
          acc[item.provider] = item;
          return acc;
        }, {});
        this.setState({ itemsByProvider: map, loading: false });
      } else {
        throw new Error(res.error?.message || "Failed to load integrations");
      }
    } catch (err) {
      const msg = `${err.message}${err.requestId ? ` (RID: ${err.requestId})` : ""}`;
      this.setState({ loading: false, error: msg });
    }
    // Phase 31.5B: load tracked sites alongside standard integrations.
    this.fetchTrackedSites();
  }

  async fetchTrackedSites() {
    try {
      const res = await client.integrationsSitesList();
      if (res?.ok) this.setState({ trackedSites: res.items || [] });
    } catch (_) { /* non-critical — UI degrades gracefully */ }
  }

  async fetchBrainWriteMode() {
    try {
      const res = await client.request('/api/settings/brain-write-approval-mode');
      if (res && (res.mode === 'review' || res.mode === 'instant')) {
        this.setState({ brainWriteMode: res.mode });
      }
    } catch (_) { /* non-critical — default stays 'review' */ }
  }

  async saveBrainWriteMode(mode) {
    const prev = this.state.brainWriteMode;
    // Optimistic: update immediately + show saving status; revert on failure
    this.setState({ brainWriteMode: mode, brainWriteSavingStatus: 'Saving…' });
    try {
      const res = await client.request('/api/settings/brain-write-approval-mode', {
        method: 'PUT',
        body: { mode },
      });
      if (!res || !res.ok) throw new Error(res?.error || 'save failed');
      this.setState({ brainWriteSavingStatus: 'Saved ✓' });
      setTimeout(() => {
        if (this.state.brainWriteSavingStatus === 'Saved ✓') {
          this.setState({ brainWriteSavingStatus: '' });
        }
      }, 2200);
    } catch (err) {
      console.error('[BRAIN_WRITE_MODE] save failed:', err.message);
      this.setState({ brainWriteMode: prev, brainWriteSavingStatus: 'Save failed — try again' });
    }
  }

  async fetchAiSettings() {
    try {
      this.setState({ aiLoading: true, aiError: null });
      const res = await client.getAiSettings();
      if (res.ok) {
        this.setState({
          aiSettings: res.item,
          aiDraftMode: res.item.mode,
          aiLoading: false
        });
      } else {
        throw new Error(res.error?.message || "Failed to load AI Settings");
      }
    } catch (err) {
      console.error('[AI Settings] Load error:', err.message, err.requestId || '');
      this.setState({ aiLoading: false, aiError: 'Unable to load AI settings. Please try again.' });
    }
  }

  async fetchScheduleRules() {
    try {
      const res = await client.getScheduleRules().catch(() => ({ rules: [] }));
      this.scheduleRules = res.rules || [];
    } catch {
      this.scheduleRules = [];
    }
  }

  async fetchAccountData() {
    try {
      const res = await client.authMe();
      if (res.ok) {
        this.setState({ accountData: res.user });
      }
    } catch (e) {
      // non-fatal — form renders empty
    }
  }

  async fetchBillingStatus() {
    try {
      const res = await client.getBillingStatus();
      this.setState({ billingData: res });
    } catch (e) {
      // non-fatal
    }
  }

  async loadBrands() {
    this.setState({ brandsLoading: true });
    try {
      const res = await client.getBrands();
      this.setState({ brands: res.brands || [], brandsLoading: false });
      this.loadBrandShares();
      this.loadStockPolicy();
    } catch (e) {
      this.setState({ brandsLoading: false });
    }
  }

  async loadStockPolicy() {
    const brandId = localStorage.getItem('heist_active_brand') || (this.state.brands && this.state.brands[0] && this.state.brands[0].id);
    if (!brandId) return;
    try {
      const res = await client.getStockPolicy(brandId);
      this.setState({ stockPolicy: res.policy || null, stockBlocklist: res.blocklist || [] });
    } catch (e) { /* non-fatal */ }
  }

  async loadBrandShares() {
    try {
      const res = await client.getBrandShares();
      this.setState({ brandShares: res.shares || [] });
    } catch (e) { /* non-fatal */ }
  }

  async loadWorkspace() {
    this.setState({ workspaceLoading: true });
    try {
      const res = await client.getWorkspace();
      if (res.workspace) {
        this.setState({
          workspace: res.workspace,
          workspaceMembers: res.members || [],
          pendingInvites: res.pendingInvites || [],
          myWorkspaceRole: res.myRole,
          workspaceLoading: false
        });
        // Load agency brain profile
        try {
          const brainRes = await client.getAgencyBrain();
          if (brainRes.profile) this.setState({ agencyBrain: brainRes.profile });
        } catch(e) { /* no agency brain */ }
      } else {
        this.setState({ workspaceLoading: false });
      }
    } catch (e) {
      this.setState({ workspaceLoading: false });
    }
  }

  renderBrandsSection() {
    const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    if (this.state.brandsLoading) return '<div style="padding:20px;color:var(--text-muted);">Loading brands...</div>';
    const brands = this.state.brands || [];
    const activeBrandId = localStorage.getItem('heist_active_brand');
    return `
      <div class="brands-list">
        ${brands.map(b => `
          <div class="brand-card ${b.id === activeBrandId ? 'brand-card--active' : ''}" data-brand-id="${b.id}">
            <div class="brand-card__icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--primary, #8b5cf6)" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>
            <div class="brand-card__info">
              <div class="brand-card__name">${_esc(b.name)}</div>
              <div class="brand-card__meta">
                Created ${new Date(b.created_at).toLocaleDateString()}
                ${b.id === activeBrandId ? ' &middot; <strong style="color:var(--success);">Active</strong>' : ''}
              </div>
            </div>
            <div class="brand-card__actions">
              <button class="btn btn-ghost btn-sm ${b.publishing_paused ? 'btn-warning-text' : ''}" data-action="toggle-pause" data-brand-id="${b.id}" title="${b.publishing_paused ? 'Resume publishing' : 'Pause all publishing'}">
                ${b.publishing_paused
                  ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21"/></svg> Resume'
                  : '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg> Pause'}
              </button>
              ${b.id !== activeBrandId ? `<button class="btn btn-ghost btn-sm" data-action="switch-brand" data-brand-id="${b.id}" title="Switch to this brand">Switch</button>` : ''}
              <button class="btn btn-ghost btn-sm" data-action="rename-brand" data-brand-id="${b.id}" data-brand-name="${_esc(b.name)}" title="Rename">Rename</button>
              ${this.state.workspace && ['owner','admin'].includes(this.state.myWorkspaceRole) && b.id === activeBrandId ? `<button class="btn btn-ghost btn-sm" data-action="share-brand" data-brand-id="${b.id}" title="Share with client">Share</button>` : ''}
              ${brands.length > 1 ? `<button class="btn btn-ghost btn-sm btn-danger-text" data-action="delete-brand" data-brand-id="${b.id}" data-brand-name="${_esc(b.name)}" title="Delete">Delete</button>` : ''}
            </div>
            ${b.publishing_paused ? `<div class="set-paused-notice"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:middle;margin-right:4px;"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>Publishing paused — scheduled posts will not be published</div>` : ''}
          </div>
        `).join('')}
      </div>
      <div style="margin-top:16px;">
        <button class="btn btn-primary btn-sm" data-action="new-brand" id="btn-new-brand">+ New Brand Brain</button>
        <span style="margin-left:12px;font-size:12px;color:var(--text-muted);">${brands.length} brand${brands.length !== 1 ? 's' : ''}</span>
      </div>
      ${this.state.workspace && ['owner','admin'].includes(this.state.myWorkspaceRole) && this.state.brandShares?.length ? `
        <div style="margin-top:24px;">
          <h3 class="t-h3" style="margin-bottom:12px;">Client Share Links</h3>
          ${this.state.brandShares.map(s => {
            const statusClass = s.revoked ? 'revoked' : (!s.active ? 'expired' : 'active');
            const statusLabel = s.revoked ? 'Revoked' : (!s.active ? 'Expired' : 'Active');
            return `<div class="share-link-row">
              <span class="share-link-row__label">${_esc(s.label || 'Unnamed link')}</span>
              <span class="share-link-row__status share-link-row__status--${statusClass}">${statusLabel}</span>
              <span style="font-size:11px;color:var(--text-muted);">${s.comment_count || 0} comments</span>
              ${s.active ? `<button class="btn btn-ghost btn-sm" data-action="copy-share-link" data-url="${_esc(s.url)}" title="Copy link">Copy</button>
              <button class="btn btn-ghost btn-sm btn-danger-text" data-action="revoke-share" data-share-id="${s.id}" title="Revoke">Revoke</button>` : ''}
            </div>`;
          }).join('')}
        </div>
      ` : ''}
      ${this.renderStockPolicyPanel(activeBrandId)}
    `;
  }

  renderStockPolicyPanel(activeBrandId) {
    const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    const p = this.state.stockPolicy;
    const bl = this.state.stockBlocklist || [];
    if (!p) return '';
    const orient = p.orientation || '';
    return `
      <div style="margin-top:28px;padding-top:20px;border-top:1px solid var(--border);">
        <h3 class="t-h3" style="margin-bottom:6px;">Stock media curation</h3>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:14px;">Steer which stock images & videos get auto-picked for this brand — everywhere (autopilot, blog auto-curation, the media picker, and AI agents via MCP).</p>
        <div style="display:flex;flex-direction:column;gap:14px;max-width:620px;">
          <label style="display:block;">
            <span style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Preferred themes <span style="color:var(--text-muted);font-weight:400;">(comma-separated — biases every search)</span></span>
            <input type="text" class="input" id="stock-preferred" value="${_esc((p.preferredTerms||[]).join(', '))}" placeholder="candid, authentic, diverse, natural light" style="width:100%;font-size:13px;padding:8px 10px;border-radius:6px;box-sizing:border-box;" />
          </label>
          <label style="display:block;">
            <span style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Banned terms <span style="color:var(--text-muted);font-weight:400;">(results matching these are dropped)</span></span>
            <input type="text" class="input" id="stock-banned" value="${_esc((p.bannedTerms||[]).join(', '))}" placeholder="office, laptop, desk, handshake, suit" style="width:100%;font-size:13px;padding:8px 10px;border-radius:6px;box-sizing:border-box;" />
          </label>
          <div style="display:flex;gap:20px;flex-wrap:wrap;align-items:center;">
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer;">
              <input type="checkbox" id="stock-avoid-people" ${p.avoidPeople ? 'checked' : ''} /> Avoid photos of people
            </label>
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer;">
              <input type="checkbox" id="stock-src-pexels" ${p.sources && p.sources.pexels ? 'checked' : ''} /> Pexels
            </label>
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;cursor:pointer;">
              <input type="checkbox" id="stock-src-unsplash" ${p.sources && p.sources.unsplash ? 'checked' : ''} /> Unsplash
            </label>
            <label style="display:flex;align-items:center;gap:6px;font-size:13px;">
              Orientation
              <select id="stock-orientation" style="font-size:13px;padding:5px 8px;border:1px solid var(--border);border-radius:6px;background:var(--surface-2);color:var(--text);">
                <option value="" ${orient===''?'selected':''}>Any</option>
                <option value="landscape" ${orient==='landscape'?'selected':''}>Landscape</option>
                <option value="portrait" ${orient==='portrait'?'selected':''}>Portrait</option>
                <option value="square" ${orient==='square'?'selected':''}>Square</option>
              </select>
            </label>
          </div>
          <div>
            <button class="btn btn-primary btn-sm" data-action="save-stock-policy" data-brand-id="${activeBrandId || ''}">Save curation rules</button>
          </div>
          <div>
            <div style="font-size:12px;font-weight:600;margin-bottom:6px;">Blocked images <span style="color:var(--text-muted);font-weight:400;">(${bl.length}) — never auto-used again</span></div>
            ${bl.length ? bl.map(b => `
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
                <a href="${_esc(b.url)}" target="_blank" rel="noopener" style="font-size:11px;color:var(--primary);text-decoration:none;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${_esc(b.url)}</a>
                <button class="btn btn-ghost btn-sm btn-danger-text" data-action="unblock-stock" data-block-id="${b.id}" data-brand-id="${activeBrandId || ''}">Unblock</button>
              </div>`).join('') : '<div style="font-size:12px;color:var(--text-muted);">None yet. Block an image from the media picker or via the assistant.</div>'}
          </div>
        </div>
      </div>
    `;
  }

  async loadApprovals() {
    this.setState({ approvalsLoading: true });
    try {
      const res = await client.getApprovals({ status: this.state.approvalFilter });
      this.setState({ approvals: res.approvals || [], approvalsLoading: false });
    } catch(e) {
      this.setState({ approvals: [], approvalsLoading: false });
    }
  }

  async loadActivity(append) {
    this.setState({ activityLoading: true });
    try {
      const params = { limit: 50, offset: append ? this.state.activityOffset : 0 };
      if (this.state.activityBrandFilter) params.brand_id = this.state.activityBrandFilter;
      if (this.state.activityActionFilter) params.action = this.state.activityActionFilter;
      const res = await client.getActivityLog(params);
      const entries = res.entries || [];
      if (append) {
        this.setState({ activityEntries: [...this.state.activityEntries, ...entries], activityLoading: false });
      } else {
        this.setState({ activityEntries: entries, activityLoading: false, activityOffset: 0 });
      }
    } catch(e) {
      this.setState({ activityEntries: append ? this.state.activityEntries : [], activityLoading: false });
    }
  }

  renderActivitySection() {
    const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    const ACTION_VERBS = {
      content_created: 'created content', content_edited: 'edited content', content_deleted: 'deleted content',
      content_published: 'published content', content_submitted_approval: 'submitted content for approval',
      brand_created: 'created a brand', brand_renamed: 'renamed a brand', brand_deleted: 'deleted a brand',
      member_invited: 'invited a team member', member_joined: 'joined the workspace',
      member_role_changed: 'changed a member role', member_removed: 'removed a team member',
      approval_approved: 'approved content', approval_rejected: 'rejected content',
      agency_brain_updated: 'updated the Agency Brain'
    };
    const ACTION_ICONS = {
      content_created: '\u{1F4DD}', content_edited: '\u270F\uFE0F', content_deleted: '\u{1F5D1}\uFE0F',
      content_published: '\u{1F680}', content_submitted_approval: '\u{1F4E4}',
      brand_created: '\u2728', brand_renamed: '\u{1F3F7}\uFE0F', brand_deleted: '\u274C',
      member_invited: '\u{1F4E9}', member_joined: '\u{1F44B}',
      member_role_changed: '\u{1F511}', member_removed: '\u{1F6AB}',
      approval_approved: '\u2705', approval_rejected: '\u{1F6A9}',
      agency_brain_updated: '\u{1F9E0}'
    };
    function relTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z'));
      const diff = Date.now() - d.getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'Just now';
      if (mins < 60) return mins + 'm ago';
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return hrs + 'h ago';
      const days = Math.floor(hrs / 24);
      if (days === 1) return 'Yesterday';
      if (days < 7) return days + 'd ago';
      return d.toLocaleDateString();
    }

    const brands = (this.state.brands || []).filter(b => b.name !== '__agency_brain__');
    const actions = Object.keys(ACTION_VERBS);

    let html = `<div class="activity-filters">
      <select data-action="activity-brand-filter">
        <option value="">All brands</option>
        ${brands.map(b => `<option value="${b.id}" ${this.state.activityBrandFilter === b.id ? 'selected' : ''}>${_esc(b.name)}</option>`).join('')}
      </select>
      <select data-action="activity-action-filter">
        <option value="">All actions</option>
        ${actions.map(a => `<option value="${a}" ${this.state.activityActionFilter === a ? 'selected' : ''}>${ACTION_VERBS[a]}</option>`).join('')}
      </select>
    </div>`;

    if (this.state.activityLoading && this.state.activityEntries.length === 0) {
      return html + '<div style="padding:20px;color:var(--text-muted);">Loading activity...</div>';
    }

    if (this.state.activityEntries.length === 0) {
      return html + '<div style="padding:32px;text-align:center;color:var(--text-muted);font-size:13px;">No activity yet. Actions from your team will appear here.</div>';
    }

    html += '<div class="activity-list">';
    for (const entry of this.state.activityEntries) {
      const icon = ACTION_ICONS[entry.action] || '\u{1F4CB}';
      const verb = ACTION_VERBS[entry.action] || entry.action;
      const who = _esc(entry.user_name || entry.user_email || 'Unknown');
      const brandPill = entry.brand_name && entry.brand_name !== '__agency_brain__'
        ? `<span class="activity-entry__brand">${_esc(entry.brand_name)}</span>` : '';
      html += `<div class="activity-entry">
        <span class="activity-entry__icon">${icon}</span>
        <div class="activity-entry__body">
          <div class="activity-entry__text"><strong>${who}</strong> ${verb}${brandPill}</div>
          <div class="activity-entry__time">${relTime(entry.created_at)}</div>
        </div>
      </div>`;
    }
    html += '</div>';

    if (this.state.activityEntries.length >= 50 && this.state.activityEntries.length % 50 === 0) {
      html += `<div class="activity-load-more"><button class="btn btn-ghost btn-sm" data-action="activity-load-more">${this.state.activityLoading ? 'Loading...' : 'Load More'}</button></div>`;
    }

    return html;
  }

  renderApprovalsSection() {
    const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    if (this.state.approvalsLoading) return '<div style="padding:20px;color:var(--text-muted);">Loading approvals...</div>';

    const f = this.state.approvalFilter;
    const pending = this.state.approvals.filter(a => a.status === 'pending');
    const items = this.state.approvals;

    const selectedCount = this.state.approvalSelectedIds?.size || 0;
    const visiblePendingIds = items.filter(a => a.status === 'pending').map(a => a.id);
    const allVisibleSelected = visiblePendingIds.length > 0 &&
        visiblePendingIds.every(id => this.state.approvalSelectedIds.has(id));

    return `
      <div class="approval-tabs">
        <button class="approval-tab ${f === 'pending' ? 'active' : ''}" data-action="approval-filter" data-filter="pending">
          Pending ${pending.length > 0 ? `<span class="approval-tab__count">${pending.length}</span>` : ''}
        </button>
        <button class="approval-tab ${f === 'approved' ? 'active' : ''}" data-action="approval-filter" data-filter="approved">Approved (7d)</button>
        <button class="approval-tab ${f === 'rejected' ? 'active' : ''}" data-action="approval-filter" data-filter="rejected">Rejected (7d)</button>
        <button class="approval-tab ${f === 'all' ? 'active' : ''}" data-action="approval-filter" data-filter="all">All</button>
      </div>

      ${visiblePendingIds.length > 0 ? `
        <div class="approval-bulk-toolbar" style="display:flex;align-items:center;gap:12px;padding:8px 12px;margin:8px 0 12px;background:var(--surface-2);border:1px solid var(--border);border-radius:8px;">
          <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-muted);cursor:pointer;">
            <input type="checkbox" data-action="approval-select-all" ${allVisibleSelected ? 'checked' : ''} aria-label="Select all pending approvals">
            ${selectedCount > 0 ? `<strong>${selectedCount} selected</strong>` : 'Select all pending'}
          </label>
          ${selectedCount > 0 ? `
            <span style="flex:1"></span>
            <button class="btn btn-primary btn-sm" data-action="approve-selected">Approve ${selectedCount}</button>
            <button class="btn btn-ghost btn-sm" style="color:var(--danger);" data-action="reject-selected">Reject ${selectedCount}</button>
            <button class="btn btn-ghost btn-sm" data-action="approval-clear-selection" aria-label="Clear selection">Clear</button>
          ` : ''}
        </div>
      ` : ''}

      ${items.length === 0 ? `
        <div style="text-align:center;padding:40px 20px;color:var(--text-muted);">
          <p class="t-body">${f === 'pending' ? 'No pending approvals — your team\'s content is all clear.' : 'No approvals found for this filter.'}</p>
        </div>
      ` : items.map(a => {
        const preview = _esc((a.content || '').substring(0, 140));
        const platform = _esc(a.platform || 'post');
        const brand = _esc(a.brand_name || 'Unknown');
        const submitter = _esc(a.submitter_name || a.submitter_email || 'Team member');
        const submitted = a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : '';
        const reviewer = _esc(a.reviewer_name || '');
        const reviewed = a.reviewed_at ? new Date(a.reviewed_at).toLocaleDateString() : '';
        const note = _esc(a.review_note || '');

        const isSelectable = a.status === 'pending';
        const isSelected = isSelectable && this.state.approvalSelectedIds.has(a.id);
        return `<div class="approval-card${isSelected ? ' approval-card--selected' : ''}">
          ${isSelectable ? `
            <input type="checkbox" data-action="approval-toggle-select" data-id="${a.id}" ${isSelected ? 'checked' : ''}
                   aria-label="Select for bulk action" style="margin:14px 0 0 14px;">
          ` : ''}
          <div class="approval-card__content">
            <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
              <span class="role-badge" style="font-size:11px;">${brand}</span>
              <span style="font-size:12px;font-weight:600;color:var(--text);">${platform}</span>
              ${a.content_title ? `<span style="font-size:12px;color:var(--text-muted);">— ${_esc(a.content_title)}</span>` : ''}
            </div>
            <div class="approval-card__preview">${preview}</div>
            <div class="approval-card__meta">
              <span>By ${submitter}</span>
              <span>${submitted}</span>
              ${a.status !== 'pending' ? `<span style="text-transform:capitalize;font-weight:600;color:${a.status === 'approved' ? 'var(--success)' : 'var(--danger)'};">${a.status}</span>` : ''}
              ${reviewer && a.status !== 'pending' ? `<span>Reviewed by ${reviewer} on ${reviewed}</span>` : ''}
            </div>
            ${note ? `<div class="approval-card__note approval-card__note--${a.status}">"${note}"</div>` : ''}
          </div>
          ${a.status === 'pending' ? `
            <div class="approval-card__actions">
              <button class="btn btn-primary btn-sm" data-action="approve-content" data-id="${a.id}">Approve</button>
              <button class="btn btn-ghost btn-sm" style="color:var(--danger);" data-action="reject-content" data-id="${a.id}">Reject</button>
            </div>
          ` : ''}
        </div>`;
      }).join('')}
    `;
  }

  renderTeamSection() {
    const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    if (this.state.workspaceLoading) return '<div style="padding:20px;color:var(--text-muted);">Loading team...</div>';

    const ws = this.state.workspace;
    const user = window.__auth__;
    const tier = user?.tier || 'trial';
    const isWorkspaceTier = ['founder', 'agency'].includes(tier) || ['admin', 'angel_admin', 'god_admin'].includes(user?.role);

    // No workspace yet — show setup
    if (!ws) {
      if (!isWorkspaceTier) {
        return `<div style="padding:24px;text-align:center;">
          <p style="color:var(--text-muted);margin-bottom:12px;">Team collaboration is available on Founder and Agency plans.</p>
          <a href="#upgrade" class="btn btn-primary btn-sm">Upgrade to unlock</a>
        </div>`;
      }
      return `<div style="padding:24px;text-align:center;">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" stroke-width="1.5" style="margin-bottom:12px;"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        <h3 class="t-h3" style="margin-bottom:8px;">Create Your Workspace</h3>
        <p style="color:var(--text-muted);margin-bottom:16px;font-size:13px;">Set up a workspace to invite team members and manage brand access.</p>
        <button class="btn btn-primary" data-action="create-workspace">Create Workspace</button>
      </div>`;
    }

    const isOwnerAdmin = ['owner', 'admin'].includes(this.state.myWorkspaceRole);
    const members = this.state.workspaceMembers || [];
    const invites = this.state.pendingInvites || [];
    const brands = this.state.brands || [];
    const seatPct = ws.seatLimit ? Math.round((ws.seatsUsed / ws.seatLimit) * 100) : 0;
    const barClass = seatPct >= 100 ? 'seat-bar__fill--full' : seatPct >= 80 ? 'seat-bar__fill--warn' : '';

    let html = '';

    // Seat usage
    html += `<div style="margin-bottom:24px;">
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <span style="font-size:13px;color:var(--text-muted);">${ws.seatsUsed} of ${ws.seatLimit} seats used</span>
        <span class="role-badge role-badge--${this.state.myWorkspaceRole}">${this.state.myWorkspaceRole}</span>
      </div>
      <div class="seat-bar"><div class="seat-bar__fill ${barClass}" style="width:${Math.min(seatPct, 100)}%"></div></div>
      ${ws.tier === 'agency' && this.state.myWorkspaceRole === 'owner' && ws.seatLimit < 50 ? `
        <button class="btn btn-ghost btn-sm" data-action="add-seats" style="margin-top:8px;">+ Add Seats ($10/seat/mo)</button>
      ` : ''}
    </div>`;

    // Agency Brain editor (owner/admin only)
    if (isOwnerAdmin) {
      const ab = this.state.agencyBrain || {};
      html += `<div class="agency-brain-card">
        <div class="agency-brain-card__header">
          <div class="agency-brain-card__icon"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" stroke-width="1.75"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>
          <div>
            <div class="agency-brain-card__title">Agency Brain</div>
            <div class="agency-brain-card__desc">Shared voice and guidelines inherited by all brand brains (brands can override)</div>
          </div>
        </div>
        <div class="onboard-field" style="margin-bottom:12px;">
          <label class="onboard-label">Shared Mission</label>
          <textarea class="onboard-input" id="ab-mission" rows="2" placeholder="Agency-wide mission...">${_esc(ab.mission || '')}</textarea>
        </div>
        <div class="onboard-field" style="margin-bottom:12px;">
          <label class="onboard-label">Shared Audience</label>
          <textarea class="onboard-input" id="ab-audience" rows="2" placeholder="Agency-wide target audience...">${_esc(ab.audience || '')}</textarea>
        </div>
        <div class="onboard-field" style="margin-bottom:12px;">
          <label class="onboard-label">Shared Voice / Tone</label>
          <input class="onboard-input" id="ab-voice" placeholder="e.g. Professional, Warm" value="${_esc(ab.voice || '')}">
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;">
          <div class="onboard-field" style="flex:1;min-width:200px;">
            <label class="onboard-label">Keywords</label>
            <input class="onboard-input" id="ab-keywords" placeholder="growth, innovation..." value="${_esc(ab.keywords || '')}">
          </div>
          <div class="onboard-field" style="flex:1;min-width:200px;">
            <label class="onboard-label">Avoid Words</label>
            <input class="onboard-input" id="ab-avoid" placeholder="cheap, spam..." value="${_esc(ab.avoid_words || '')}">
          </div>
        </div>
        <button class="btn btn-primary btn-sm" data-action="save-agency-brain">Save Agency Brain</button>
        <span id="agency-brain-msg" style="margin-left:12px;font-size:12px;"></span>
      </div>`;
    }

    // Team members list
    html += `<h3 class="t-h3" style="margin:24px 0 12px;">Team Members</h3>`;
    html += `<div class="team-member-list">`;
    for (const m of members) {
      const initials = (m.name || m.email || '?').substring(0, 2).toUpperCase();
      html += `<div class="team-member-card">
        <div class="team-member-card__avatar">${_esc(initials)}</div>
        <div class="team-member-card__info">
          <div class="team-member-card__name">${_esc(m.name || m.email)}</div>
          <div class="team-member-card__email">${_esc(m.email)}${m.lastActive ? ` &middot; Active ${new Date(m.lastActive).toLocaleDateString()}` : ''}</div>
        </div>
        <span class="role-badge role-badge--${m.role}">${m.role}</span>
        ${isOwnerAdmin && m.role !== 'owner' ? `
          <div class="team-member-card__actions">
            <select class="btn btn-ghost btn-sm" data-action="change-role" data-user-id="${m.userId}" style="font-size:11px;padding:4px 8px;">
              ${['admin','editor','viewer'].map(r => `<option value="${r}" ${r === m.role ? 'selected' : ''}>${r}</option>`).join('')}
            </select>
            <button class="btn btn-ghost btn-sm" data-action="reset-member-pwd" data-user-id="${m.userId}" data-name="${_esc(m.name || m.email)}" title="Reset password">Reset Pwd</button>
            <button class="btn btn-ghost btn-sm btn-danger-text" data-action="remove-member" data-user-id="${m.userId}" data-name="${_esc(m.name || m.email)}">Remove</button>
          </div>
        ` : ''}
      </div>`;
    }
    html += `</div>`;

    // Invite form (owner/admin)
    if (isOwnerAdmin) {
      html += `<h3 class="t-h3" style="margin:24px 0 12px;">Invite Team Member</h3>`;
      html += `<div class="invite-form">
        <input class="onboard-input" id="invite-email" type="email" placeholder="team@example.com">
        <select class="onboard-select" id="invite-role">
          <option value="editor">Editor</option>
          <option value="admin">Admin</option>
          <option value="viewer">Viewer</option>
        </select>
        <button class="btn btn-primary btn-sm" data-action="invite-member">Send Invitation</button>
      </div>
      <span id="invite-msg" style="display:block;margin-top:8px;font-size:12px;"></span>`;
    }

    // Pending invitations
    if (invites.length > 0) {
      html += `<h3 class="t-h3" style="margin:24px 0 12px;">Pending Invitations</h3>`;
      for (const inv of invites) {
        const expires = new Date(inv.expires_at).toLocaleDateString();
        html += `<div class="pending-invite">
          <span class="pending-invite__email">${_esc(inv.email)}</span>
          <span class="pending-invite__role">${inv.role}</span>
          <span class="pending-invite__expiry">Expires ${expires}</span>
          ${isOwnerAdmin ? `<button class="btn btn-ghost btn-sm btn-danger-text" data-action="revoke-invite" data-invite-id="${inv.id}">Revoke</button>` : ''}
        </div>`;
      }
    }

    return html;
  }

  renderBillingContent() {
    const b = this.state.billingData;
    if (!b) return '<div style="padding:24px;color:var(--text-muted);font-size:13px;">Loading billing data...</div>';

    // T-07 — prices + feature lists come from the shared plan config
    // (app/src/data/plans.json), same source as the Upgrade page. Do not
    // hardcode plan copy here again.
    const PRICES = Object.fromEntries(PLANS.plans.map(p => [p.id, { monthly: p.monthly, annual: p.annual }]));
    const FEATURES = Object.fromEntries(PLANS.plans.map(p => [p.id, p.features]));
    const cycle = this.state.billingCycle;
    const tierLabel = (b.tier || 'trial').charAt(0).toUpperCase() + (b.tier || 'trial').slice(1);
    const isTrial = b.tier === 'trial';
    const postPct = b.usage?.posts?.limit ? Math.min(100, Math.round((b.usage.posts.used / b.usage.posts.limit) * 100)) : 0;

    return `
      <div class="billing-current-card">
        <div class="billing-current-left">
          <div class="billing-plan-name">${tierLabel} Plan</div>
          <div class="billing-plan-price">${isTrial ? 'Free trial' : PRICES[b.tier] ? '$' + PRICES[b.tier][b.billing_cycle || 'monthly'] + '/' + (b.billing_cycle === 'annual' ? 'yr' : 'mo') : 'Custom'}</div>
          ${isTrial && b.trial_days_left !== null ? `
            <div class="billing-trial-notice">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              ${b.trial_days_left} day${b.trial_days_left !== 1 ? 's' : ''} remaining in your trial
            </div>
          ` : ''}
        </div>
        <div class="billing-current-right">
          <div class="billing-usage-item">
            <div class="billing-usage-label">Posts this month</div>
            <div class="billing-usage-bar"><div class="billing-usage-fill" style="width:${postPct}%"></div></div>
            <div class="billing-usage-text">${b.usage?.posts?.used || 0}${b.usage?.posts?.limit ? ' / ' + b.usage.posts.limit : ' / unlimited'}</div>
          </div>
          <div class="billing-usage-item">
            <div class="billing-usage-label">Active campaigns</div>
            <div class="billing-usage-text">${b.usage?.campaigns?.active || 0}${b.usage?.campaigns?.limit ? ' / ' + b.usage.campaigns.limit : ' / unlimited'}</div>
          </div>
        </div>
      </div>

      <div class="billing-cycle-wrap">
        <div class="billing-cycle-toggle">
          <button class="billing-cycle-btn ${cycle === 'monthly' ? 'active' : ''}" data-billing-cycle="monthly">Monthly</button>
          <button class="billing-cycle-btn ${cycle === 'annual' ? 'active' : ''}" data-billing-cycle="annual">Annual <span class="billing-save-badge">Save 17%</span></button>
        </div>
      </div>

      <div class="billing-tiers">
        ${['starter', 'pro', 'founder', 'agency'].map(t => `
          <div class="billing-tier-card ${b.tier === t ? 'billing-tier-card--current' : ''} ${t === 'pro' ? 'billing-tier-card--popular' : ''}">
            ${t === 'pro' ? '<div class="billing-popular-badge">Most Popular</div>' : ''}
            <div class="billing-tier-name">${t.charAt(0).toUpperCase() + t.slice(1)}</div>
            <div class="billing-tier-price">$${PRICES[t][cycle]}<span>/mo</span></div>
            ${cycle === 'annual' ? '<div class="billing-tier-annual">Billed annually</div>' : ''}
            <ul class="billing-tier-features">
              ${FEATURES[t].map(f => `<li>${f}</li>`).join('')}
            </ul>
            ${b.tier === t ?
              '<button class="btn btn-glass" disabled>Current Plan</button>' :
              `<button class="btn btn-primary btn-sm" data-action="upgrade" data-tier="${t}" data-cycle="${cycle}">
                ${isTrial ? 'Start Plan' : 'Switch to ' + t.charAt(0).toUpperCase() + t.slice(1)}
              </button>`
            }
          </div>
        `).join('')}
      </div>

      ${b.has_stripe ? `
        <div class="billing-manage">
          <button class="btn btn-glass" data-action="open-portal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            Manage Subscription & Payment Methods
          </button>
          <p class="billing-manage-hint">Update payment method, download invoices, or cancel your subscription through the Stripe portal.</p>
        </div>
      ` : ''}
    `;
  }

  setState(newState) {
    this.state = { ...this.state, ...newState };
    this.render();
  }

  renderSkeleton() {
    if (!this.el) return;
    this.el.innerHTML = `
      <div class="settings-page">
        <header class="page-header">
          <div class="page-shell">
            <div class="header-content">
              <div class="header-left">
                <div class="skeleton skeleton-text" style="width:100px;height:12px;margin-bottom:8px;"></div>
                <div class="skeleton skeleton-title" style="width:140px;"></div>
                <div class="skeleton skeleton-text" style="width:200px;margin-top:6px;"></div>
              </div>
            </div>
          </div>
        </header>
        <div class="page-shell page-content" style="display:flex;gap:24px;">
          <div style="width:180px;flex-shrink:0;">
            <div class="skeleton skeleton-row" style="height:36px;margin-bottom:8px;border-radius:8px;"></div>
            <div class="skeleton skeleton-row" style="height:36px;margin-bottom:8px;border-radius:8px;"></div>
            <div class="skeleton skeleton-row" style="height:36px;margin-bottom:8px;border-radius:8px;"></div>
            <div class="skeleton skeleton-row" style="height:36px;margin-bottom:8px;border-radius:8px;"></div>
          </div>
          <div style="flex:1;">
            <div class="skeleton skeleton-card" style="height:200px;margin-bottom:16px;border-radius:12px;"></div>
            <div class="skeleton skeleton-card" style="height:160px;border-radius:12px;"></div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.el) return;

    // Reset content
    this.el.innerHTML = `
      <div class="settings-page fade-in">
        ${renderPageHeader({
          eyebrow: "Configuration",
          title: "Settings",
          subtitle: "Configure your HQ.",
          rightHtml: `
            <button id="settings-take-tour" class="btn btn-glass btn-sm" type="button" data-testid="settings-help-tour-trigger">
              Take a tour of Settings
            </button>
          `,
        })}

        <div class="settings-layout settings-layout-tabbed page-shell page-content">
          ${(() => {
            // Single source of truth for the nav structure — used by both
            // the desktop sticky rail and the mobile dropdown.
            const groups = [
              { key: 'connections', label: 'Connections', items: (() => {
                const base = [
                  { id: 'section-integrations', label: 'Integrations' },
                  { id: 'section-ai-settings', label: 'AI Settings' },
                ];
                if (isProTier()) {
                  base.push({ id: 'section-agent-inbox', label: 'Agent Inbox' });
                }
                return base;
              })()},
              { key: 'content', label: 'Content', items: [
                { id: 'schedule-rules-section', label: 'Schedule' },
                { id: 'section-brands', label: 'Brands' }
              ]},
              { key: 'account', label: 'Account', items: (() => {
                const acctItems = [
                  { id: 'section-account', label: 'Profile' },
                  { id: 'section-billing', label: 'Billing' },
                  { id: 'section-team-management', label: 'Team' }
                ];
                if (this.state.workspace && ['owner','admin'].includes(this.state.myWorkspaceRole)) {
                  acctItems.push({ id: 'section-approvals', label: 'Approvals' });
                  acctItems.push({ id: 'section-activity', label: 'Activity' });
                }
                return acctItems;
              })()},
              { key: 'privacy', label: 'Privacy', items: [
                { id: 'section-team', label: 'Data & Privacy' }
              ]}
            ];
            const active = this.state.activeSection;

            // Find the active item label for the mobile trigger.
            const activeItem = groups.flatMap(g => g.items).find(i => i.id === active);
            const activeLabel = activeItem ? activeItem.label : 'Settings';

            // Desktop: flat sticky rail with group labels (no collapsibility).
            const desktopNav = `
              <nav class="settings-nav">
                ${groups.map(g => `
                  <div class="settings-nav-group expanded">
                    <div class="settings-nav-group-label">${g.label}</div>
                    <div class="settings-nav-group-items">
                      ${g.items.map(it =>
                        `<button class="settings-nav-item ${active === it.id ? 'active' : ''}" data-section="${it.id}">${it.label}</button>`
                      ).join('')}
                    </div>
                  </div>
                `).join('')}
              </nav>
            `;

            // Mobile: sticky-top dropdown trigger + sheet (rendered inline,
            // hidden by CSS until .open is set).
            const mobileNav = `
              <div class="settings-mobile-nav">
                <button class="settings-mobile-nav__trigger" data-action="toggle-mobile-nav" aria-expanded="${this.state.mobileNavOpen ? 'true' : 'false'}">
                  <span class="settings-mobile-nav__label">${activeLabel}</span>
                  <span class="settings-mobile-nav__caret" aria-hidden="true">▾</span>
                </button>
                ${this.state.mobileNavOpen ? `
                  <div class="settings-mobile-nav__sheet">
                    ${groups.map(g => `
                      <div class="settings-mobile-nav__group">
                        <div class="settings-mobile-nav__label-line">${g.label}</div>
                        ${g.items.map(it =>
                          `<button class="settings-mobile-nav__item ${active === it.id ? 'active' : ''}" data-section="${it.id}">${it.label}</button>`
                        ).join('')}
                      </div>
                    `).join('')}
                  </div>
                ` : ''}
              </div>
            `;
            return desktopNav + mobileNav;
          })()}

          <div class="settings-panel">
            <section class="h-pane account-section ${this.state.activeSection === 'section-account' ? 'section-active' : ''}" id="section-account">
              ${renderPaneHead('section-account', { eyebrow: 'Account', title: 'Profile', lede: `Your name, photo, email, and password. Personal — these don't change anything brand-related.` })}
              ${this.state.helpChipOpen === 'section-account' ? renderHelpPopover('section-account') : ''}
              <div class="h-pane-body">
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Profile photo</div>
                    <div class="h-row-label__hint">JPG, PNG, GIF · Max 5MB. Shown in the topbar and the user menu.</div>
                  </div>
                  <div class="h-row-control">
                    <div class="account-photo-row" style="margin:0;padding:0;border:0;">
                      <div class="account-avatar" id="settings-avatar">
                        ${this.state.accountData?.avatar_path
                          ? `<img src="${this.state.accountData.avatar_path}" alt="Avatar">`
                          : `<span>${(this.state.accountData?.name || this.state.aiSettings?.brandName || 'U').charAt(0).toUpperCase()}</span>`}
                      </div>
                      <button class="h-btn h-btn--ghost h-btn--sm" data-action="upload-avatar" type="button">Upload photo</button>
                      <input type="file" id="avatar-file-input" accept="image/jpeg,image/png,image/gif,image/webp" style="display:none;">
                    </div>
                  </div>
                </div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Full name</div>
                    <div class="h-row-label__hint">How Heist refers to you in the UI and emails.</div>
                  </div>
                  <div class="h-row-control">
                    <input class="h-input" type="text" id="account-name"
                      value="${(this.state.accountData?.name || '').replace(/"/g, '&quot;')}"
                      placeholder="Your name" autocomplete="name">
                  </div>
                </div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Email</div>
                    <div class="h-row-label__hint">Used for sign-in and notifications. Contact support to change.</div>
                  </div>
                  <div class="h-row-control">
                    <input class="h-input" type="email"
                      value="${(this.state.accountData?.email || '').replace(/"/g, '&quot;')}"
                      readonly autocomplete="email">
                  </div>
                </div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Time zone</div>
                    <div class="h-row-label__hint">Drives Schedule rules and the Calendar's local time display.</div>
                  </div>
                  <div class="h-row-control">
                    <select class="h-select" id="account-timezone">
                      ${['UTC','America/New_York','America/Chicago','America/Los_Angeles','America/Denver','Europe/London','Europe/Paris','Asia/Tokyo','Asia/Shanghai','Australia/Sydney'].map(tz => `
                        <option value="${tz}" ${(this.state.accountData?.timezone || 'UTC') === tz ? 'selected' : ''}>${tz}</option>
                      `).join('')}
                    </select>
                  </div>
                </div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Role</div>
                    <div class="h-row-label__hint">Set by your workspace owner.</div>
                  </div>
                  <div class="h-row-control">
                    <input class="h-input" type="text" value="${this.state.accountData?.role || 'user'}" readonly>
                  </div>
                </div>
                <div class="h-save-row">
                  <button class="h-btn h-btn--primary h-btn--sm" data-action="account-save" ${this.state.accountSaving ? 'disabled' : ''}>
                    ${this.state.accountSaving ? 'Saving…' : 'Save changes'}
                  </button>
                  ${this.state.accountMsg
                    ? `<span class="h-save-msg h-save-msg--${this.state.accountMsg.type}">${this.state.accountMsg.text}</span>`
                    : ''}
                </div>

                <hr class="h-divider">

                <div class="h-eyebrow" style="margin-bottom:14px;">Password & security</div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Current password</div>
                    <div class="h-row-label__hint">Required when setting a new password.</div>
                  </div>
                  <div class="h-row-control">
                    <input class="h-input" type="password" id="account-current-pw" placeholder="••••••••" autocomplete="current-password">
                  </div>
                </div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">New password</div>
                    <div class="h-row-label__hint">Minimum 8 characters.</div>
                  </div>
                  <div class="h-row-control">
                    <input class="h-input" type="password" id="account-new-pw" placeholder="Min. 8 characters" autocomplete="new-password">
                  </div>
                </div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Confirm new password</div>
                  </div>
                  <div class="h-row-control">
                    <input class="h-input" type="password" id="account-confirm-pw" placeholder="Re-enter new password" autocomplete="new-password">
                  </div>
                </div>
                <div class="h-save-row">
                  <button class="h-btn h-btn--ghost h-btn--sm" data-action="password-save" ${this.state.passwordSaving ? 'disabled' : ''}>
                    ${this.state.passwordSaving ? 'Updating…' : 'Update password'}
                  </button>
                  ${this.state.passwordMsg
                    ? `<span class="h-save-msg h-save-msg--${this.state.passwordMsg.type}">${this.state.passwordMsg.text}</span>`
                    : ''}
                </div>
              </div>
            </section>

            <section class="h-pane account-section ${this.state.activeSection === 'section-brand' ? 'section-active' : ''}" id="section-brand">
              ${renderPaneHead('section-brand', { eyebrow: 'Account', title: 'Workspace', lede: 'Your workspace identity — logo and display name.' })}
              <div class="h-pane-body">
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Logo</div>
                    <div class="h-row-label__hint">Recommended 512×512 PNG or SVG. Shown in the sidebar.</div>
                  </div>
                  <div class="h-row-control">
                    <div class="account-photo-row" style="margin:0;padding:0;border:0;">
                      <div class="account-avatar" style="border-radius: 12px; font-size: 18px; font-weight: 800;">
                        <span>${(this.state.aiSettings?.brandName || 'CP').substring(0,2).toUpperCase()}</span>
                      </div>
                      <button class="h-btn h-btn--ghost h-btn--sm" data-action="upload-logo" type="button">Upload logo</button>
                      <button class="h-btn h-btn--ghost h-btn--sm" data-action="remove-logo" type="button">Remove</button>
                    </div>
                  </div>
                </div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Brand name</div>
                    <div class="h-row-label__hint">How your workspace is referred to throughout Heist.</div>
                  </div>
                  <div class="h-row-control">
                    <input class="h-input" type="text" id="brand-name"
                      value="${(this.state.aiSettings?.brandName || '').replace(/"/g, '&quot;')}"
                      placeholder="Your brand name">
                  </div>
                </div>
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Plan</div>
                    <div class="h-row-label__hint">Manage in Billing.</div>
                  </div>
                  <div class="h-row-control">
                    <input class="h-input" type="text" value="Enterprise" readonly>
                  </div>
                </div>
                <div class="h-save-row">
                  <button class="h-btn h-btn--primary h-btn--sm" data-action="brand-save">Save changes</button>
                  ${this.state.brandSaveMsg
                    ? `<span class="h-save-msg h-save-msg--${this.state.brandSaveMsg.type}">${this.state.brandSaveMsg.text}</span>`
                    : ''}
                </div>
              </div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-integrations' ? 'section-active' : ''}" id="section-integrations">
              ${renderPaneHead('section-integrations', { eyebrow: 'Connections', title: 'Integrations', lede: 'Connect your social accounts so Heist can publish for you. Each connection is a one-time OAuth handshake.' })}
              ${this.state.helpChipOpen === 'section-integrations' ? renderHelpPopover('section-integrations') : ''}
              <div class="h-pane-body">${this.renderContent()}</div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-ai-settings' ? 'section-active' : ''}" id="section-ai-settings">
              ${renderPaneHead('section-ai-settings', { eyebrow: 'Connections', title: 'AI Settings', lede: 'Pick whether Heist generates with our shared keys or your own. BYOK gives you cost control and bypasses the monthly token caps.' })}
              ${this.state.helpChipOpen === 'section-ai-settings' ? renderHelpPopover('section-ai-settings') : ''}
              <div class="h-pane-body">${this.renderAiSettingsSection()}</div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-agent-inbox' ? 'section-active' : ''}" id="section-agent-inbox">
              ${renderPaneHead('section-agent-inbox', {
                eyebrow: 'Connections',
                title: 'Agent Inbox',
                lede: 'Drafts written by your AI chat queue here for review.',
              })}
              ${this.state.helpChipOpen === 'section-agent-inbox' ? renderHelpPopover('section-agent-inbox') : ''}
              <div class="h-pane-body">${renderAgentInboxPrimer()}</div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'schedule-rules-section' ? 'section-active' : ''}" id="schedule-rules-section">
              ${renderPaneHead('schedule-rules-section', { eyebrow: 'Content', title: 'Posting Schedule', lede: 'Define posting windows so auto-fill can slot drafts onto the calendar — e.g. "Tuesdays + Thursdays at 9am, never weekends."' })}
              ${this.state.helpChipOpen === 'schedule-rules-section' ? renderHelpPopover('schedule-rules-section') : ''}
              <div class="h-pane-body">${this.renderScheduleRulesSection()}</div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-team' ? 'section-active' : ''}" id="section-team">
              ${renderPaneHead('section-team', { eyebrow: 'Privacy', title: 'Data & Privacy', lede: 'Export your data as JSON, or delete your account. Heist never trains models on your content.' })}
              ${this.state.helpChipOpen === 'section-team' ? renderHelpPopover('section-team') : ''}
              <div class="h-pane-body">
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title">Export my data</div>
                    <div class="h-row-label__hint">Download a JSON file with all your content, campaigns, Brain data, and chat history.</div>
                  </div>
                  <div class="h-row-control">
                    <button class="h-btn h-btn--ghost" data-action="export-data">Download my data</button>
                  </div>
                </div>
                <hr class="h-divider">
                <div class="h-row">
                  <div class="h-row-label">
                    <div class="h-row-label__title" style="color: var(--danger);">Delete my account</div>
                    <div class="h-row-label__hint">Permanently deletes your account and all associated data. This cannot be undone.</div>
                  </div>
                  <div class="h-row-control">
                    <button class="h-btn h-btn--danger" data-action="delete-account">Delete my account</button>
                  </div>
                </div>
              </div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-billing' ? 'section-active' : ''}" id="section-billing">
              ${renderPaneHead('section-billing', { eyebrow: 'Account', title: 'Billing & Subscription', lede: 'Plan, billing cycle, payment method, and invoice history. Upgrades take effect immediately.' })}
              ${this.state.helpChipOpen === 'section-billing' ? renderHelpPopover('section-billing') : ''}
              <div class="h-pane-body">${this.renderBillingContent()}</div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-brands' ? 'section-active' : ''}" id="section-brands">
              ${renderPaneHead('section-brands', { eyebrow: 'Content', title: 'Brand Brains', lede: 'Each brand has its own Brain, library, calendar, and integrations. Switch between them or add a new one.' })}
              ${this.state.helpChipOpen === 'section-brands' ? renderHelpPopover('section-brands') : ''}
              <div class="h-pane-body">${this.renderBrandsSection()}</div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-team-management' ? 'section-active' : ''}" id="section-team-management">
              ${renderPaneHead('section-team-management', { eyebrow: 'Account', title: 'Team', lede: 'Invite teammates and assign roles (Owner, Admin, Editor, Viewer). Available on Pro and above.' })}
              ${this.state.helpChipOpen === 'section-team-management' ? renderHelpPopover('section-team-management') : ''}
              <div class="h-pane-body">${this.renderTeamSection()}</div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-approvals' ? 'section-active' : ''}" id="section-approvals">
              ${renderPaneHead('section-approvals', { eyebrow: 'Account', title: 'Content Approvals', lede: 'Review and approve drafts your team submitted before they go live.' })}
              ${this.state.helpChipOpen === 'section-approvals' ? renderHelpPopover('section-approvals') : ''}
              <div class="h-pane-body">${this.renderApprovalsSection()}</div>
            </section>

            <section class="h-pane settings-section ${this.state.activeSection === 'section-activity' ? 'section-active' : ''}" id="section-activity">
              ${renderPaneHead('section-activity', { eyebrow: 'Account', title: 'Activity Log', lede: 'A timeline of who did what in your workspace — created, edited, scheduled, published, deleted.' })}
              ${this.state.helpChipOpen === 'section-activity' ? renderHelpPopover('section-activity') : ''}
              <div class="h-pane-body">${this.renderActivitySection()}</div>
            </section>
          </div>
        </div>
      </div>
    `;

  }

  renderContent() {
    const { loading, error, message } = this.state;

    if (loading && Object.keys(this.state.itemsByProvider).length === 0) {
      return `<div class="loading-spinner">Loading integrations...</div>`;
    }

    let html = "";

    if (error) {
      html += `
        <div class="alert alert-error">
          <span class="icon">⚠️</span> ${error}
          <button class="btn-retry" data-action="retry">Retry</button>
        </div>
      `;
    }

    if (message) {
      html += `
        <div class="alert alert-${message.type}">
          <span class="icon">${message.type === "success" ? "✅" : "⚠️"}</span> ${message.text}
        </div>
      `;
    }

    // Core publishing integrations (Meta, X, LinkedIn, WordPress) — first-class,
    // always-on connectors. Ship-blocking if any of these break.
    html += `<div class="int-grid">`;
    html += this.renderProviderCard("meta", "Meta (Instagram / Facebook)", "One connection covers both. Instagram requires a linked Facebook Business Page (Meta's rule — we won't post to FB unless you schedule for it).");
    // T-08 — honest expectation-setting: X credentials are per-user (your own
    // developer app's keys), and X's Free API tier cannot post. Without a paid
    // tier on YOUR app, every scheduled X post fails with AUTH_FAILED.
    html += this.renderProviderCard("x", "X (Twitter)", "Posting requires a paid X API tier on your own X developer account — X's Free tier can't post via the API.");
    html += this.renderProviderCard("linkedin", "LinkedIn", "Connect Company Page");
    html += this.renderProviderCard("wordpress", "WordPress", "Connect hosted or self-hosted site with App Password");
    html += `</div>`;

    // Secondary platforms — video-first (TikTok/YouTube) and own-domain (Website).
    // Use a distinct grid class so the .int-grid selector doesn't include these
    // cards in its count — only the four core publishing connectors live there.
    html += `<h3 class="section-title" style="margin-top:32px;">Video &amp; Website</h3>`;
    html += `<div class="int-grid-secondary">`;
    html += this.renderProviderCard("tiktok", "TikTok", "Connect Creator or Business Account");
    html += this.renderProviderCard("youtube", "YouTube", "Connect YouTube Channel");
    html += this.renderProviderCard("website", "Website", "Publish to your own domain");
    html += `</div>`;

    // Discovery & communities — Pinterest (visual search) and Reddit (community).
    // Both connect via OAuth and post with a per-post destination (board /
    // subreddit) chosen in the composer.
    html += `<h3 class="section-title" style="margin-top:32px;">Discovery &amp; Communities</h3>`;
    html += `<div class="int-grid-secondary">`;
    html += this.renderProviderCard("pinterest", "Pinterest", "Connect to publish Pins. Pick a board per post.");
    html += this.renderProviderCard("reddit", "Reddit", "Connect to submit posts. Pick a subreddit per post.");
    html += `</div>`;

    // Phase 31.5B: Website + Blog analytics integrations.
    // These are separate from the publishing connectors above — they add a
    // tracked site so we can count pageviews (pixel mode) or monitor uptime
    // (url_monitor mode).
    html += this._renderSiteAnalyticsIntegrations();

    // Knowledge Sources section
    html += `
      <h3 class="section-title" style="margin-top:32px;">Connected Knowledge Sources</h3>
      <p class="section-subtitle" style="margin-bottom:16px;">Sync documents from external services into your Brain vault.</p>
      <div class="ks-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px;">
        <div class="ks-card int-card-style">
          <div class="int-card-header">
            <h3 class="int-card-name">Google Docs</h3>
            <p class="int-card-desc">Sync Google Documents into your Brain</p>
          </div>
          <div class="int-card-status ${this._googleConnected ? 'int-status--connected' : 'int-status--disconnected'}">
            <span class="int-status-dot"></span>
            ${this._googleConnected ? 'Connected' : 'Not Connected'}
          </div>
          <div class="int-card-actions">
            ${this._googleConnected
              ? '<button class="btn btn-primary btn-sm" data-action="pick-google-docs">Select Docs to Sync</button><button class="btn btn-secondary btn-sm" data-action="disconnect-google-docs" style="margin-left:6px">Disconnect</button>'
              : '<button class="btn btn-primary btn-sm" data-action="connect-google-docs">Connect Google Docs</button>'
            }
          </div>
        </div>
        <div class="ks-card int-card-style">
          <div class="int-card-header">
            <h3 class="int-card-name">Notion</h3>
            <p class="int-card-desc">Sync Notion pages into your Brain</p>
          </div>
          <div class="int-card-status ${this._notionConnected ? 'int-status--connected' : 'int-status--disconnected'}">
            <span class="int-status-dot"></span>
            ${this._notionConnected ? 'Connected' : 'Not Connected'}
          </div>
          <div class="int-card-actions">
            ${this._notionConnected
              ? '<button class="btn btn-primary btn-sm" data-action="pick-notion-pages">Select Pages to Sync</button><button class="btn btn-secondary btn-sm" data-action="disconnect-notion" style="margin-left:6px">Disconnect</button>'
              : '<button class="btn btn-primary btn-sm" data-action="connect-notion">Connect Notion</button>'
            }
          </div>
        </div>
      </div>
    `;

    return html;
  }

  renderAiSettingsSection() {
    const { aiLoading, aiError, aiMessage, aiSettings } = this.state;
    const isAdmin = ['admin', 'angel_admin', 'god_admin'].includes(window.__auth__?.role);

    if (aiLoading && !aiSettings) {
      return `<div class="loading-spinner">Loading AI settings...</div>`;
    }

    let html = '';
    if (aiError) html += `<div class="ai-alert ai-alert--error">${aiError}<button class="btn-int btn-int--secondary" data-action="ai-retry" style="margin-left:auto;">Retry</button></div>`;
    if (aiMessage) html += `<div class="ai-alert ai-alert--${aiMessage.type}">${aiMessage.text}</div>`;

    const providers = aiSettings?.providers || { openai: {}, anthropic: {}, google: {} };
    const currentModel = aiSettings?.current_model || 'gpt-4o-mini';

    const PROVIDER_CONFIG = [
      { key: 'openai', label: 'OpenAI', desc: 'GPT models \u2014 fast, versatile, industry standard', placeholder: 'sk-...', helpUrl: 'platform.openai.com',
        models: [ { id: 'gpt-4o-mini', label: 'GPT-4o Mini', desc: 'Fast, cost-effective' }, { id: 'gpt-4o', label: 'GPT-4o', desc: 'High quality, balanced' }, { id: 'gpt-4.1', label: 'GPT-4.1', desc: 'Latest flagship' }, { id: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', desc: 'Fast, smart' }, { id: 'gpt-4.1-nano', label: 'GPT-4.1 Nano', desc: 'Ultra fast' }, { id: 'o3-mini', label: 'o3-mini', desc: 'Reasoning model' } ], defaultModel: 'gpt-4o-mini' },
      { key: 'anthropic', label: 'Anthropic', desc: 'Claude models \u2014 creative, nuanced, strong at writing', placeholder: 'sk-ant-...', helpUrl: 'console.anthropic.com',
        models: [ { id: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4', desc: 'Creative, nuanced' }, { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', desc: 'Fast, affordable' } ], defaultModel: 'claude-sonnet-4-20250514' },
      { key: 'google', label: 'Google AI', desc: 'Gemini models \u2014 efficient, multimodal-ready', placeholder: 'AIza...', helpUrl: 'aistudio.google.com',
        models: [ { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', desc: 'Fast, efficient' }, { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', desc: 'Advanced reasoning' } ], defaultModel: 'gemini-2.5-flash' },
      { key: 'openrouter', label: 'OpenRouter', desc: 'Free + paid models from every vendor \u2014 lowest cost, deepest menu', placeholder: 'sk-or-...', helpUrl: 'openrouter.ai/keys',
        models: [
          { id: 'meta-llama/llama-3.3-70b-instruct:free', label: 'Llama 3.3 70B (free)', desc: 'Best free chat model' },
          { id: 'mistralai/mistral-7b-instruct:free', label: 'Mistral 7B (free)', desc: 'Fast, lightweight, free' },
          { id: 'google/gemini-2.0-flash-exp:free', label: 'Gemini 2.0 Flash exp (free)', desc: 'Free tier from Google' },
          { id: 'anthropic/claude-sonnet-4', label: 'Claude Sonnet 4 via OpenRouter', desc: 'Paid \u2014 same Sonnet, OpenRouter routing' },
          { id: 'openai/gpt-4o-mini', label: 'GPT-4o Mini via OpenRouter', desc: 'Paid \u2014 OpenAI through OpenRouter' },
        ], defaultModel: 'meta-llama/llama-3.3-70b-instruct:free' }
    ];

    function providerForModel(modelId) { for (const p of PROVIDER_CONFIG) { if (p.models.some(m => m.id === modelId)) return p.key; } return 'openai'; }
    const activeProvider = providerForModel(currentModel);

    // Sort providers so the active (primary) provider appears first
    const sortedProviders = [...PROVIDER_CONFIG].sort((a, b) => {
      if (a.key === activeProvider) return -1;
      if (b.key === activeProvider) return 1;
      return 0;
    });

    // Top-level model selector — lists every available model across providers.
    // Sits above the per-provider cards so smoke tests can find a single
    // selector via #ai-model-select without mapping to a specific provider.
    html += `
      <div class="ai-model-select-row" style="margin-bottom:16px;">
        <label class="t-label">Primary Model</label>
        <select id="ai-model-select" class="ai-prov-model-select">
          ${PROVIDER_CONFIG.flatMap(p => p.models.map(m => `<option value="${m.id}" ${currentModel === m.id ? 'selected' : ''}>${p.label} \u2014 ${m.label}</option>`)).join('')}
        </select>
      </div>
    `;

    html += '<div class="ai-provider-cards">';
    for (const p of sortedProviders) {
      const prov = providers[p.key] || {};
      const hasKey = prov.has_key;
      const hint = prov.key_hint;
      const isActive = activeProvider === p.key;
      const selectedModel = isActive ? currentModel : p.defaultModel;

      html += `
      <div class="ai-provider-card ai-provider-card-v2 ${isActive ? 'ai-provider-card-v2--active' : ''}" data-provider="${p.key}" data-ai-provider="${p.key}">
        <div class="ai-prov-header">
          <div class="ai-prov-header__left">
            <h3 class="ai-prov-name">${p.label}</h3>
            <p class="ai-prov-desc">${p.desc}</p>
          </div>
          <div class="ai-prov-status ${hasKey ? (isActive ? 'ai-prov-status--primary' : 'ai-prov-status--connected') : 'ai-prov-status--none'}">
            <span class="ai-prov-status__dot"></span>
            ${isActive ? 'Primary' + (hint ? ' (' + hint + ')' : '') : hasKey ? 'Saved' + (hint ? ' (' + hint + ')' : '') : 'No key'}
          </div>
        </div>
        <div class="ai-prov-body">
          <div class="ai-prov-key-row">
            <div class="ai-prov-key-wrap">
              <input type="password" class="ai-prov-key-input" placeholder="${hasKey ? 'Enter new key to overwrite' : p.placeholder}" data-provider-key="${p.key}" />
              <button type="button" class="ai-prov-eye-toggle" data-action="toggle-key-visibility" data-provider="${p.key}" title="Show/hide key">
                <svg class="eye-open" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg class="eye-closed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </div>
            <button class="btn btn-glass btn-sm" data-action="save-provider-key" data-provider="${p.key}">Save</button>
            ${hasKey ? `<button class="btn btn-ghost-subtle btn-sm set-btn-danger-clear" data-action="clear-provider-key" data-provider="${p.key}">Clear</button>` : ''}
          </div>
          <div class="ai-prov-model-row">
            <label class="ai-prov-model-label">Model</label>
            <select class="ai-prov-model-select" data-action="select-provider-model" data-provider="${p.key}" ${!hasKey ? 'disabled' : ''}>
              ${p.models.map(m => `<option value="${m.id}" ${selectedModel === m.id ? 'selected' : ''}>${m.label} \u2014 ${m.desc}</option>`).join('')}
            </select>
            ${!hasKey ? '<span class="ai-prov-model-hint">Add your API key to unlock</span>' : ''}
          </div>
          ${hasKey ? `
          <div class="ai-prov-activate-row">
            ${isActive
              ? '<span class="ai-prov-primary-label"><span class="ai-prov-status__dot" style="background:var(--success)"></span> Primary Provider</span>'
              : `<button class="btn btn-glass btn-sm" data-action="activate-provider" data-provider="${p.key}">Use This Provider</button>`
            }
          </div>
          ` : ''}
        </div>
        <p class="ai-prov-help">Get your key at <a href="https://${p.helpUrl}" target="_blank" rel="noopener">${p.helpUrl}</a></p>
      </div>`;
    }
    html += '</div>';

    // Replicate — media generation only (not text LLM)
    const replicateProv = providers.replicate || {};
    const replicateHasKey = replicateProv.has_key;
    const replicateHint = replicateProv.key_hint;
    html += `
    <div class="ai-media-gen-section" style="margin-top:1.5rem;">
      <h3 class="t-h3" style="margin:0 0 0.5rem;">AI Media Generation</h3>
      <p class="t-hint" style="margin:0 0 1rem;">Generate images and videos from post descriptions. Images use your text AI key (OpenAI/Google) or Replicate. Videos require Replicate.</p>
      <div class="ai-provider-card-v2" data-ai-provider="replicate">
        <div class="ai-prov-header">
          <div class="ai-prov-header__left">
            <h3 class="ai-prov-name">Replicate</h3>
            <p class="ai-prov-desc">Flux image models + AI video generation (Minimax, Kling)</p>
          </div>
          <div class="ai-prov-status ${replicateHasKey ? 'ai-prov-status--connected' : 'ai-prov-status--none'}">
            <span class="ai-prov-status__dot"></span>
            ${replicateHasKey ? 'Saved' + (replicateHint ? ' (' + replicateHint + ')' : '') : 'No key'}
          </div>
        </div>
        <div class="ai-prov-body">
          <div class="ai-prov-key-row">
            <div class="ai-prov-key-wrap">
              <input type="password" class="ai-prov-key-input" placeholder="${replicateHasKey ? 'Enter new key to overwrite' : 'r8_...'}" data-provider-key="replicate" />
              <button type="button" class="ai-prov-eye-toggle" data-action="toggle-key-visibility" data-provider="replicate" title="Show/hide key">
                <svg class="eye-open" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg class="eye-closed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </div>
            <button class="btn btn-glass btn-sm" data-action="save-provider-key" data-provider="replicate">Save</button>
            ${replicateHasKey ? `<button class="btn btn-ghost-subtle btn-sm set-btn-danger-clear" data-action="clear-provider-key" data-provider="replicate">Clear</button>` : ''}
          </div>
        </div>
        <p class="ai-prov-help">Get your key at <a href="https://replicate.com/account/api-tokens" target="_blank" rel="noopener">replicate.com/account/api-tokens</a></p>
      </div>
    </div>`;

    // Faceless Video (Phase 37) — ElevenLabs voiceover + Creatomate slideshow render.
    // Both keys are required for the faceless video pipeline to work end-to-end.
    const elProv = providers.elevenlabs || {};
    const elHasKey = elProv.has_key;
    const elHint = elProv.key_hint;
    const cmProv = providers.creatomate || {};
    const cmHasKey = cmProv.has_key;
    const cmHint = cmProv.key_hint;
    const rwProv = providers.runway || {};
    const rwHasKey = rwProv.has_key;
    const rwHint = rwProv.key_hint;
    html += `
    <div class="ai-faceless-video-section" style="margin-top:1.5rem;">
      <h3 class="t-h3" style="margin:0 0 0.5rem;">Faceless Video</h3>
      <p class="t-hint" style="margin:0 0 1rem;">Generate script-driven slideshow videos with AI voiceover. Both keys are required.</p>
      <div class="ai-provider-card-v2" data-ai-provider="elevenlabs">
        <div class="ai-prov-header">
          <div class="ai-prov-header__left">
            <h3 class="ai-prov-name">ElevenLabs</h3>
            <p class="ai-prov-desc">AI voiceover — natural-sounding voices for your scripts</p>
          </div>
          <div class="ai-prov-status ${elHasKey ? 'ai-prov-status--connected' : 'ai-prov-status--none'}">
            <span class="ai-prov-status__dot"></span>
            ${elHasKey ? 'Saved' + (elHint ? ' (' + elHint + ')' : '') : 'No key'}
          </div>
        </div>
        <div class="ai-prov-body">
          <div class="ai-prov-key-row">
            <div class="ai-prov-key-wrap">
              <input type="password" class="ai-prov-key-input" placeholder="${elHasKey ? 'Enter new key to overwrite' : 'sk_...'}" data-provider-key="elevenlabs" />
              <button type="button" class="ai-prov-eye-toggle" data-action="toggle-key-visibility" data-provider="elevenlabs" title="Show/hide key">
                <svg class="eye-open" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg class="eye-closed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </div>
            <button class="btn btn-glass btn-sm" data-action="save-provider-key" data-provider="elevenlabs">Save</button>
            ${elHasKey ? `<button class="btn btn-ghost-subtle btn-sm set-btn-danger-clear" data-action="clear-provider-key" data-provider="elevenlabs">Clear</button>` : ''}
          </div>
        </div>
        <p class="ai-prov-help">Get your key at <a href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noopener">elevenlabs.io/app/settings/api-keys</a></p>
      </div>
      <div class="ai-provider-card-v2" data-ai-provider="creatomate" style="margin-top:0.75rem;">
        <div class="ai-prov-header">
          <div class="ai-prov-header__left">
            <h3 class="ai-prov-name">Creatomate</h3>
            <p class="ai-prov-desc">Video composition — assembles voiceover + images into the final MP4</p>
          </div>
          <div class="ai-prov-status ${cmHasKey ? 'ai-prov-status--connected' : 'ai-prov-status--none'}">
            <span class="ai-prov-status__dot"></span>
            ${cmHasKey ? 'Saved' + (cmHint ? ' (' + cmHint + ')' : '') : 'No key'}
          </div>
        </div>
        <div class="ai-prov-body">
          <div class="ai-prov-key-row">
            <div class="ai-prov-key-wrap">
              <input type="password" class="ai-prov-key-input" placeholder="${cmHasKey ? 'Enter new key to overwrite' : 'Your Creatomate API key'}" data-provider-key="creatomate" />
              <button type="button" class="ai-prov-eye-toggle" data-action="toggle-key-visibility" data-provider="creatomate" title="Show/hide key">
                <svg class="eye-open" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg class="eye-closed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </div>
            <button class="btn btn-glass btn-sm" data-action="save-provider-key" data-provider="creatomate">Save</button>
            ${cmHasKey ? `<button class="btn btn-ghost-subtle btn-sm set-btn-danger-clear" data-action="clear-provider-key" data-provider="creatomate">Clear</button>` : ''}
          </div>
        </div>
        <p class="ai-prov-help">Get your key at <a href="https://creatomate.com/account/api-keys" target="_blank" rel="noopener">creatomate.com/account/api-keys</a></p>
      </div>

      <div class="ai-faceless-substep" style="margin-top:1.25rem;padding-top:0.75rem;border-top:1px solid var(--border);">
        <h4 class="t-h4" style="margin:0 0 0.25rem;font-size:13px;">+ AI-video style (optional)</h4>
        <p class="t-hint" style="margin:0 0 0.75rem;font-size:11px;">Generates cinematic B-roll clips instead of a stock-image slideshow. Pricier per render, but a more premium look. Falls back to the slideshow style if Runway is not connected.</p>
        <div class="ai-provider-card-v2" data-ai-provider="runway">
          <div class="ai-prov-header">
            <div class="ai-prov-header__left">
              <h3 class="ai-prov-name">Runway</h3>
              <p class="ai-prov-desc">Gen-4 Turbo image-to-video — short cinematic clips chained into the final cut</p>
            </div>
            <div class="ai-prov-status ${rwHasKey ? 'ai-prov-status--connected' : 'ai-prov-status--none'}">
              <span class="ai-prov-status__dot"></span>
              ${rwHasKey ? 'Saved' + (rwHint ? ' (' + rwHint + ')' : '') : 'No key'}
            </div>
          </div>
          <div class="ai-prov-body">
            <div class="ai-prov-key-row">
              <div class="ai-prov-key-wrap">
                <input type="password" class="ai-prov-key-input" placeholder="${rwHasKey ? 'Enter new key to overwrite' : 'Your Runway API key'}" data-provider-key="runway" />
                <button type="button" class="ai-prov-eye-toggle" data-action="toggle-key-visibility" data-provider="runway" title="Show/hide key">
                  <svg class="eye-open" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg class="eye-closed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
              <button class="btn btn-glass btn-sm" data-action="save-provider-key" data-provider="runway">Save</button>
              ${rwHasKey ? `<button class="btn btn-ghost-subtle btn-sm set-btn-danger-clear" data-action="clear-provider-key" data-provider="runway">Clear</button>` : ''}
            </div>
          </div>
          <p class="ai-prov-help">Get your key at <a href="https://dev.runwayml.com/" target="_blank" rel="noopener">dev.runwayml.com</a> (look for "API Keys")</p>
        </div>
      </div>
    </div>`;

    // Admin: Server AI Configuration (collapsed)
    if (isAdmin) {
      const serverPolicy = this.state.serverKeyPolicy || 'none';
      const serverKeys = this.state.serverKeys || {};
      const policyLabels = { none: 'Not shared \u2014 server keys are unused', admins_only: 'Admins only \u2014 only admin accounts use server keys as fallback', all_users: "All users \u2014 everyone can use server keys when they don't have their own" };

      html += `
      <details class="ai-server-config" id="ai-server-config">
        <summary class="ai-server-toggle">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          Server AI Configuration
          <span class="ai-server-admin-badge">Admin</span>
        </summary>
        <div class="ai-server-body">
          <p class="ai-server-desc">Server keys are the fallback when a user doesn't have their own key for a provider. Control who has access below.</p>
          <div class="ai-server-keys-status">
            <div class="ai-server-key-item"><span>OpenAI</span><span class="ai-prov-status ${serverKeys.openai ? 'ai-prov-status--connected' : 'ai-prov-status--none'}"><span class="ai-prov-status__dot"></span>${serverKeys.openai ? 'Configured in .env' : 'Not configured'}</span></div>
            <div class="ai-server-key-item"><span>Anthropic</span><span class="ai-prov-status ${serverKeys.anthropic ? 'ai-prov-status--connected' : 'ai-prov-status--none'}"><span class="ai-prov-status__dot"></span>${serverKeys.anthropic ? 'Configured in .env' : 'Not configured'}</span></div>
            <div class="ai-server-key-item"><span>Google AI</span><span class="ai-prov-status ${serverKeys.google ? 'ai-prov-status--connected' : 'ai-prov-status--none'}"><span class="ai-prov-status__dot"></span>${serverKeys.google ? 'Configured in .env' : 'Not configured'}</span></div>
            <div class="ai-server-key-item"><span>Replicate</span><span class="ai-prov-status ${serverKeys.replicate ? 'ai-prov-status--connected' : 'ai-prov-status--none'}"><span class="ai-prov-status__dot"></span>${serverKeys.replicate ? 'Configured in .env' : 'Not configured'}</span></div>
          </div>
          <div class="ai-server-policy">
            <label class="ai-prov-model-label">Who can use server keys as fallback?</label>
            <select class="ai-prov-model-select" data-action="change-server-policy">
              <option value="none" ${serverPolicy === 'none' ? 'selected' : ''}>Nobody \u2014 users must bring their own keys</option>
              <option value="admins_only" ${serverPolicy === 'admins_only' ? 'selected' : ''}>Admins only</option>
              <option value="all_users" ${serverPolicy === 'all_users' ? 'selected' : ''}>All users (platform-funded AI)</option>
            </select>
            <p class="ai-server-policy-status">${policyLabels[serverPolicy]}</p>
          </div>
        </div>
      </details>`;
    }

    // Brain-write approval mode — lets the user choose whether MCP agent
    // writes to the Brain land in an approval inbox or apply instantly.
    html += this.renderBrainWriteApprovalSection();

    return html;
  }

  renderBrainWriteApprovalSection() {
    const mode = this.state.brainWriteMode || 'review';
    const isReview = mode === 'review';
    const savingStatus = this.state.brainWriteSavingStatus || '';

    return `
      <section class="ai-brainwrite-section" style="margin-top:32px;padding-top:24px;border-top:1px solid var(--border);">
        <h3 class="t-h3" style="margin:0 0 6px;">How should AI changes to my Brain happen?</h3>
        <p class="settings-hint" style="color:var(--text2);font-size:13px;line-height:1.55;margin:0 0 16px;">
          When you connect Claude, ChatGPT, or another AI to Heist with Brain-write access, it can save new facts, update your brand profile, or rate how a post performed. You decide whether those changes wait for your approval or take effect right away.
        </p>

        <div class="brainwrite-radio-group" style="display:flex;flex-direction:column;gap:10px;max-width:640px;">
          <label class="brainwrite-radio ${isReview ? 'brainwrite-radio--active' : ''}" style="display:flex;gap:12px;padding:14px 16px;border:1px solid ${isReview ? 'var(--primary)' : 'var(--border)'};border-radius:10px;background:${isReview ? 'color-mix(in srgb, var(--primary) 6%, transparent)' : 'var(--surface-1)'};cursor:pointer;transition:border-color 0.15s,background 0.15s;">
            <input type="radio" name="brain-write-mode" value="review" ${isReview ? 'checked' : ''} data-action="brain-write-mode" style="margin-top:3px;flex-shrink:0;" />
            <div>
              <div style="font-weight:600;color:var(--text);margin-bottom:2px;">Review first <span style="font-weight:400;color:var(--text2);font-size:12px;">(recommended)</span></div>
              <div style="color:var(--text2);font-size:13px;line-height:1.5;">AI changes land in your Agent Inbox. Nothing changes until you approve. Best for building trust with new agents.</div>
            </div>
          </label>

          <label class="brainwrite-radio ${!isReview ? 'brainwrite-radio--active' : ''}" style="display:flex;gap:12px;padding:14px 16px;border:1px solid ${!isReview ? 'var(--primary)' : 'var(--border)'};border-radius:10px;background:${!isReview ? 'color-mix(in srgb, var(--primary) 6%, transparent)' : 'var(--surface-1)'};cursor:pointer;transition:border-color 0.15s,background 0.15s;">
            <input type="radio" name="brain-write-mode" value="instant" ${!isReview ? 'checked' : ''} data-action="brain-write-mode" style="margin-top:3px;flex-shrink:0;" />
            <div>
              <div style="font-weight:600;color:var(--text);margin-bottom:2px;">Apply instantly</div>
              <div style="color:var(--text2);font-size:13px;line-height:1.5;">AI changes take effect right away. Every change is still recorded in the audit log and you can undo it from the Agent Inbox. Best once you trust your connected agents.</div>
            </div>
          </label>
        </div>

        ${savingStatus ? `<p class="settings-hint" style="margin-top:12px;color:${savingStatus === 'Saved ✓' ? 'var(--success)' : (savingStatus === 'Save failed — try again' ? 'var(--danger)' : 'var(--text2)')};font-size:12px;">${savingStatus}</p>` : ''}

        <details class="brainwrite-disclosure" style="margin-top:16px;max-width:640px;padding:10px 14px;border:1px solid var(--border);border-radius:10px;background:var(--surface-2);">
          <summary style="cursor:pointer;font-size:13px;font-weight:500;color:var(--text);list-style:none;display:flex;align-items:center;gap:8px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.6;flex-shrink:0;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            Which AI actions does this setting control?
          </summary>
          <div style="margin-top:10px;padding-top:10px;border-top:1px solid var(--border);color:var(--text2);font-size:13px;line-height:1.6;">
            <p style="margin:0 0 8px;">This preference applies to every AI-proposed change to your Brain:</p>
            <ul style="margin:0 0 10px;padding-left:20px;">
              <li><strong style="color:var(--text);">Memory facts</strong> — <code>add_memory_fact</code>, <code>update_memory_fact</code>, <code>remove_memory_fact</code></li>
              <li><strong style="color:var(--text);">Brand profile</strong> — <code>update_brand_profile</code> (voice, audience, mission, CTA, keywords, avoid_words)</li>
              <li><strong style="color:var(--text);">Post ratings</strong> — <code>rate_post_performance</code></li>
              <li><strong style="color:var(--text);">Personas</strong> — <code>add_persona</code>, <code>update_persona</code>, <code>remove_persona</code></li>
              <li><strong style="color:var(--text);">Knowledge documents</strong> — <code>add_knowledge_document</code>, <code>remove_knowledge_document</code></li>
              <li><strong style="color:var(--text);">URL sources</strong> — <code>connect_url_source</code>, <code>disconnect_url_source</code></li>
              <li><strong style="color:var(--text);">Blog posts</strong> — <code>create_blog_post</code>, <code>update_blog_post</code>, <code>delete_blog_post</code>, <code>publish_blog_post</code></li>
              <li><strong style="color:var(--text);">Blog snippets</strong> — <code>create_blog_snippet</code>, <code>update_blog_snippet</code>, <code>delete_blog_snippet</code></li>
            </ul>
            <p style="margin:0;color:var(--text2);font-size:12px;">
              Content drafts (<code>create_draft</code>) have their own separate approval flow in the Agent Inbox — this setting doesn't change that. <code>rescan_url_source</code> and <code>generate_blog_post</code> always run immediately since they just refresh or produce drafts (no external publish).
            </p>
          </div>
        </details>
      </section>
    `;
  }


  renderScheduleRulesSection() {
    const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const PLATFORMS = ['facebook', 'instagram', 'x', 'linkedin', 'blog'];

    let rulesHtml = '';
    if (this.scheduleRules.length > 0) {
      rulesHtml = this.scheduleRules.map(rule => {
        const cadence = typeof rule.cadence_json === 'string' ? JSON.parse(rule.cadence_json) : (rule.cadence_json || []);
        const days = cadence.map(c => c.day).join(', ') || '—';
        const time = cadence[0]?.time || '—';
        return `
          <div style="display:flex; align-items:center; justify-content:space-between; padding:10px 14px; background:var(--surface-2); border:1px solid var(--border); border-radius:8px; margin-bottom:8px;">
            <div style="display:flex; gap:16px; align-items:center; font-size:13px;">
              <span style="font-weight:600; text-transform:uppercase; min-width:80px;">${rule.platform || '—'}</span>
              <span>${days}</span>
              <span style="color:var(--text-muted);">${time}</span>
              <span style="color:var(--text-muted);">max ${rule.max_posts_per_day ?? 3}/day</span>
            </div>
            <button class="set-btn-danger-link" data-action="delete-rule" data-rule-id="${rule.id}">Delete</button>
          </div>
        `;
      }).join('');
    } else {
      rulesHtml = `<div style="text-align:center; color:var(--text-muted); padding:20px; font-size:13px;">No schedule rules defined yet.</div>`;
    }

    return `
      <div style="margin-bottom:24px;">
        ${rulesHtml}
      </div>
      <div style="background:var(--surface-2); border:1px solid var(--border); border-radius:8px; padding:16px;">
        <div class="t-h3" style="margin-bottom:12px;">Add Rule</div>
        <div style="display:flex; flex-wrap:wrap; gap:12px; align-items:flex-end;">
          <div>
            <label class="t-label">Platform</label>
            <select id="rule-platform" style="padding:6px 10px; border:1px solid var(--border); border-radius:6px; font-size:13px;">
              ${PLATFORMS.map(p => `<option value="${p}">${p.charAt(0).toUpperCase() + p.slice(1)}</option>`).join('')}
            </select>
          </div>
          <div>
            <label class="t-label">Days</label>
            <div style="display:flex; gap:4px;">
              ${DAYS.map(d => `
                <label style="display:flex; align-items:center; gap:2px; font-size:12px; cursor:pointer;">
                  <input type="checkbox" class="rule-day-cb" value="${d}" /> ${d}
                </label>
              `).join('')}
            </div>
          </div>
          <div>
            <label class="t-label">Time</label>
            <input type="time" id="rule-time" value="10:00" style="padding:6px 10px; border:1px solid var(--border); border-radius:6px; font-size:13px;" />
          </div>
          <div>
            <label class="t-label">Max/Day</label>
            <input type="number" id="rule-max" value="3" min="1" max="20" style="width:60px; padding:6px 10px; border:1px solid var(--border); border-radius:6px; font-size:13px;" />
          </div>
          <button id="save-rule-btn" style="padding:6px 16px; background:var(--btn-primary-bg); color:var(--on-accent); border:none; border-radius:6px; font-size:13px; font-weight:600; cursor:pointer;">Save</button>
        </div>
      </div>
    `;
  }

  /**
   * Render the LinkedIn-specific "Post target" panel shown on connected cards.
   * Lets users pick Personal, Company, or Both, and paste a company URL.
   * Changes persist via PUT /api/integrations/linkedin/target.
   */
  _renderLinkedInTargetPanel(integration) {
    const isOpen = this.state.linkedInTargetOpen;
    const target = integration?.post_targets || 'personal';
    const orgId = integration?.organization_id || '';
    const targetLabel = target === 'both' ? 'Personal + Company'
        : target === 'organization' ? 'Company Page'
        : 'Personal profile';

    return `
      <div class="int-subpanel" style="margin-top:8px;padding:10px 12px;background:color-mix(in srgb, var(--primary) 4%, transparent);border:1px solid color-mix(in srgb, var(--primary) 12%, transparent);border-radius:8px">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px">
          <div style="font-size:12px;color:var(--text)">
            <span style="color:var(--text-muted)">Post as:</span>
            <strong style="margin-left:6px">${targetLabel}</strong>
          </div>
          <button type="button" class="btn-int btn-int--ghost" data-action="linkedin-toggle-target" style="font-size:11px;padding:3px 10px">${isOpen ? 'Cancel' : 'Change'}</button>
        </div>
        ${isOpen ? `
          <form class="linkedin-target-form" style="margin-top:10px" data-testid="linkedin-target-form">
            <div class="form-group" style="margin:0 0 8px">
              <label class="t-label">Where should posts go?</label>
              <div style="display:flex;flex-direction:column;gap:4px">
                <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer">
                  <input type="radio" name="post_targets" value="personal" ${target === 'personal' ? 'checked' : ''} />
                  Personal profile only
                </label>
                <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer">
                  <input type="radio" name="post_targets" value="organization" ${target === 'organization' ? 'checked' : ''} />
                  Company Page only
                </label>
                <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer">
                  <input type="radio" name="post_targets" value="both" ${target === 'both' ? 'checked' : ''} />
                  Both (post to personal AND company)
                </label>
              </div>
            </div>
            <div class="form-group linkedin-org-field" style="margin:0 0 8px">
              <label class="t-label">Company Page URL or ID</label>
              <input type="text" name="organization_id" value="${(orgId || '').replace(/"/g, '&quot;')}" placeholder="https://www.linkedin.com/company/12345678 or 12345678" style="width:100%;font-size:12px;padding:6px 10px" />
              <p class="text-xs text-muted" style="margin-top:4px;font-size:11px">Paste your Company Page URL — we'll extract the ID. Only needed for Company or Both targets.</p>
            </div>
            <div style="display:flex;gap:6px;justify-content:flex-end">
              <button type="submit" class="btn-int btn-int--primary" style="font-size:12px;padding:5px 12px">Save target</button>
            </div>
          </form>
        ` : ''}
      </div>
    `;
  }

  renderProviderCard(provider, name, description, opts = {}) {
    const integration = this.state.itemsByProvider[provider];
    const isConnected = integration?.status === "connected";
    // Phase 35: a "connected" integration may still be in auth_required state
    // if the underlying token has expired. Treat that as a third visual state.
    const needsReconnect = isConnected && integration?.auth_status === 'auth_required';
    let statusLabel, statusClass;
    if (needsReconnect) {
      statusLabel = "Reconnect required";
      statusClass = "int-status--auth-required";
    } else if (isConnected) {
      statusLabel = "Connected";
      statusClass = "int-status--connected";
    } else if (opts.comingSoon) {
      statusLabel = "Coming Soon";
      statusClass = "int-status--coming-soon";
    } else {
      statusLabel = "Not Connected";
      statusClass = "int-status--disconnected";
    }
    const isConnecting = this.state.connectingProvider === provider;
    const isTesting = this.state.testingProvider === provider;

    // OAuth-capable providers show a choice: OAuth (recommended) or manual credentials
    var hasOAuth = (provider === 'meta' || provider === 'linkedin' || provider === 'tiktok' || provider === 'youtube' || provider === 'wordpress' || provider === 'pinterest' || provider === 'reddit');
    // OAuth-only providers: the manual-credential path is so hostile (own Google
    // Cloud project / LinkedIn app + OAuth consent screen + refreshable tokens)
    // that surfacing it just confuses normal users. Auto-refresh on the OAuth
    // path makes it the only realistic option, so hide manual entry entirely.
    var isOAuthOnly = (provider === 'youtube' || provider === 'linkedin' || provider === 'pinterest' || provider === 'reddit');

    if (isConnecting) {
      var oauthSection = '';
      if (hasOAuth) {
        oauthSection = `
          <div class="oauth-connect-section">
            <button type="button" class="btn-int btn-int--oauth" data-action="connect-${provider}-oauth">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="margin-right:6px;vertical-align:middle;">
                <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 1.5a5.5 5.5 0 110 11 5.5 5.5 0 010-11zM7.25 4v4.5l3.5 2.1.75-1.23-2.75-1.63V4h-1.5z" fill="currentColor"/>
              </svg>
              Connect with ${provider === 'meta' ? 'Facebook Login' : provider === 'linkedin' ? 'LinkedIn OAuth' : provider === 'tiktok' ? 'TikTok Login' : provider === 'youtube' ? 'YouTube Account' : provider === 'wordpress' ? 'WordPress.com' : provider === 'pinterest' ? 'Pinterest' : provider === 'reddit' ? 'Reddit' : provider + ' OAuth'}
            </button>
            <p class="text-xs text-muted" style="margin-top:6px;">Recommended — automatic token management and refresh.</p>
            ${isOAuthOnly ? '' : '<div class="oauth-divider"><span>or enter credentials manually</span></div>'}
          </div>
        `;
      }

      if (isOAuthOnly) {
        return `
        <div class="int-card" data-provider="${provider}">
          <div class="int-card-header">
            <h3 class="int-card-name">${name}${isComingSoon(provider) ? ' <span class="coming-soon-tag">Coming Soon</span>' : ''}</h3>
            <p class="int-card-desc">${description}</p>
          </div>
          ${oauthSection}
          <div class="int-card-actions">
            <button type="button" class="btn-int btn-int--secondary" data-action="cancel-connect">Cancel</button>
          </div>
        </div>
        `;
      }

      return `
      <div class="int-card" data-provider="${provider}">
        <form class="connect-form" data-provider="${provider}">
          <div class="int-card-header">
            <h3 class="int-card-name">${name}${isComingSoon(provider) ? ' <span class="coming-soon-tag">Coming Soon</span>' : ''}</h3>
            <p class="int-card-desc">${description}</p>
          </div>
          ${oauthSection}
          <div class="form-group">
            <label>Account Label (Optional)</label>
            <input type="text" name="account_label" placeholder="e.g. Official Brand Account" value="${integration?.account_label || ''}" />
          </div>
          ${this.renderCredentialInputs(provider)}
          <div class="int-card-actions">
            <button type="button" class="btn-int btn-int--secondary" data-action="cancel-connect">Cancel</button>
            <button type="submit" class="btn-int btn-int--primary">Save Connection</button>
          </div>
        </form>
      </div>
      `;
    }

    return `
      <div class="int-card" data-provider="${provider}">
        <div class="int-card-header">
          <h3 class="int-card-name">${name}${opts.comingSoon ? ' <span class="coming-soon-tag" style="vertical-align:middle;margin-left:6px">Coming Soon</span>' : ''}</h3>
          <p class="int-card-desc">${description}${opts.comingSoon ? ' — auto-publish coming soon' : ''}</p>
        </div>
        <div class="int-card-status ${statusClass}">
          <span class="int-status-dot"></span>
          ${statusLabel}
        </div>
        ${needsReconnect ? `
          <div class="int-token-warning" style="display:flex;align-items:center;gap:8px;justify-content:space-between;">
            <span>Your ${name} token stopped working. Analytics + publishing for this connection are paused until you reconnect.</span>
            <button type="button" class="btn-int btn-int--primary" data-action="connect-${provider}${(provider==='meta'||provider==='linkedin'||provider==='tiktok'||provider==='youtube'||provider==='wordpress'||provider==='pinterest'||provider==='reddit') ? '-oauth' : ''}" style="white-space:nowrap;">Reconnect</button>
          </div>
        ` : isConnected && integration.token_expires_at ? (() => {
          // Providers whose tokens Heist refreshes automatically shouldn't
          // surface the time-based "reconnect soon" nag — the sync job picks
          // them up before expiry. If the refresh actually FAILS (revoked,
          // scope change), auth_status flips to 'auth_required' and the
          // needsReconnect branch above still fires with a real banner.
          const AUTO_REFRESH_PROVIDERS = new Set(['youtube', 'linkedin']);
          if (AUTO_REFRESH_PROVIDERS.has(provider)) return '';

          const daysLeft = Math.ceil((new Date(integration.token_expires_at) - Date.now()) / (24*60*60*1000));
          if (daysLeft <= 0) return '<div class="int-token-warning">Token expired — reconnect to resume publishing</div>';
          if (daysLeft <= 7) return `<div class="int-token-warning">Token expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — reconnect soon</div>`;
          if (daysLeft <= 30) return `<div class="int-token-info">Token valid for ${daysLeft} more days</div>`;
          return '';
        })() : ''}
        ${isConnected && provider === 'linkedin' ? this._renderLinkedInTargetPanel(integration) : ''}
        <div class="int-card-actions">
          ${!isConnected
            ? `<button class="btn-int btn-int--primary" data-action="connect" data-provider="${provider}">Connect</button>`
            : `
                <button class="btn-int btn-int--secondary" data-action="edit" data-provider="${provider}">Edit</button>
                <button class="btn-int btn-int--secondary" data-action="test" data-provider="${provider}" ${isTesting ? "disabled" : ""}>
                  ${isTesting ? "Verifying..." : "Test"}
                </button>
                <button class="btn-int btn-int--danger" data-action="disconnect" data-provider="${provider}">Disconnect</button>
              `
          }
        </div>
      </div>
    `;
  }

  // Phase 36B: secret input — when a value is already stored ("currently set"),
  // show a muted hint + placeholder telling the user they can leave it blank to
  // keep the existing credential. We never echo the secret itself back to the
  // browser (see GET /api/integrations/:provider/edit — only secret_set flags).
  _secretInput(label, name, opts = {}) {
    const isSet = !!opts.isSet;
    const placeholder = isSet ? 'Leave blank to keep current credential' : '';
    const hint = isSet
      ? `<div class="int-secret-set-hint" style="font-size:11px;color:var(--text-muted);margin-top:4px;display:flex;align-items:center;gap:4px;"><span style="letter-spacing:2px;color:var(--success)">••••••••</span> Currently set</div>`
      : '';
    return `<div class="form-group">
      <label>${label}</label>
      <div class="ai-prov-key-wrap">
        <input type="password" name="${name}" class="ai-prov-key-input" autocomplete="one-time-code" data-1p-ignore data-lpignore="true" style="width:100%" placeholder="${placeholder}" />
        <button type="button" class="ai-prov-eye-toggle" data-action="toggle-wp-password" title="Show/hide">
          <svg class="eye-open" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          <svg class="eye-closed" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
        </button>
      </div>
      ${hint}
    </div>`;
  }

  // Phase 36B: escape a pre-fill value for inline HTML attribute (`value="..."`).
  // Keeps quotes and angle brackets safe — non-secret values are things like
  // site_url, usernames, organization IDs, which can contain unusual chars.
  _prefillAttr(value) {
    if (value === undefined || value === null) return '';
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  renderCredentialInputs(provider) {
    // Phase 36B: pull pre-fill data loaded on Edit click. First-connect flow
    // has no hints yet — defaults keep the legacy blank-form behaviour.
    const hints = (this.state.editHints && this.state.editHints[provider]) || { non_secret: {}, secret_set: {} };
    const pre = hints.non_secret || {};
    const sset = hints.secret_set || {};
    const attr = (v) => this._prefillAttr(v);

    if (provider === "meta") {
      return `
        ${this._secretInput('Access Token (Page or User)', 'access_token', { isSet: !!sset.access_token })}
        <div class="form-group"><label>Facebook Page ID</label><input type="text" name="page_id" value="${attr(pre.page_id)}" autocomplete="one-time-code" data-1p-ignore data-lpignore="true" /></div>
        <div class="form-group"><label>Instagram Business Account ID</label><input type="text" name="ig_user_id" value="${attr(pre.ig_user_id)}" autocomplete="one-time-code" data-1p-ignore data-lpignore="true" /></div>
        <p class="text-xs text-muted">Access Token must have pages_manage_posts permission for Facebook. Instagram requires an Instagram Business Account linked to your Facebook Page.</p>
      `;
    }
    if (provider === "x") {
      return `
        <div class="h-callout h-callout--warn" style="margin-bottom:12px;font-size:12px;">
          <strong>Heads up:</strong> posting to X requires a <strong>paid X API tier</strong> on your own X developer account.
          These keys come from <em>your</em> app at developer.x.com — on X's Free tier, every post attempt is rejected with an auth error.
        </div>
        <p class="text-xs text-muted" style="margin-bottom:0.5rem;font-weight:600;">OAuth 1.0a (required for posting):</p>
        ${this._secretInput('API Key (Consumer Key)', 'api_key', { isSet: !!sset.api_key })}
        ${this._secretInput('API Secret (Consumer Secret)', 'api_secret', { isSet: !!sset.api_secret })}
        ${this._secretInput('Access Token', 'access_token', { isSet: !!sset.access_token })}
        ${this._secretInput('Access Token Secret', 'access_token_secret', { isSet: !!sset.access_token_secret })}
        <p class="text-xs text-muted" style="margin-bottom:0.5rem;font-weight:600;">OAuth 2.0 Bearer Token (read-only, optional):</p>
        ${this._secretInput('Bearer Token (read-only)', 'credentials_bearer_token', { isSet: !!(sset.credentials_bearer_token || sset.bearer_token) })}
        <p class="text-xs text-muted">OAuth 1.0a credentials are required to post tweets. Bearer Token is optional and used for read operations only.</p>
      `;
    }
    if (provider === "linkedin") {
      return `
        ${this._secretInput('Access Token', 'access_token', { isSet: !!sset.access_token })}
        <div class="form-group"><label>Organization ID (URN)</label><input type="text" name="organization_id" value="${attr(pre.organization_id)}" autocomplete="one-time-code" data-1p-ignore data-lpignore="true" /></div>
        <p class="text-xs text-muted">Access Token must have w_member_social or w_organization_social scope. Organization ID can be a raw numeric ID or full URN (urn:li:organization:xxxxx).</p>
      `;
    }
    if (provider === "wordpress") {
      return `
        <div class="form-group"><label>Site URL</label><input type="url" name="site_url" placeholder="https://mysite.com" value="${attr(pre.site_url)}" required autocomplete="one-time-code" data-1p-ignore data-lpignore="true" /></div>
        <div class="form-group"><label>Username</label><input type="text" name="username" value="${attr(pre.username)}" required autocomplete="one-time-code" data-1p-ignore data-lpignore="true" /></div>
        ${this._secretInput('Application Password', 'app_password', { isSet: !!sset.app_password })}
        <p class="text-xs text-muted">Generate an Application Password in WordPress under Users &rarr; Profile.</p>
    `;
    }
    if (provider === "website") {
      const blogPath = pre.blog_path !== undefined ? pre.blog_path : '/blog';
      return `
        <p class="text-xs text-muted" style="margin-bottom:12px">For Heist hosted blog. Provide a brand slug, a custom domain, or both.</p>
        <div class="form-group"><label>Brand Slug</label><input type="text" name="brand_slug" placeholder="my-brand" value="${attr(pre.brand_slug)}" autocomplete="one-time-code" data-1p-ignore data-lpignore="true" /><p class="text-xs text-muted" style="margin-top:4px">URL-friendly name for your blog: /blog/<strong>my-brand</strong>/</p></div>
        <div class="form-group" style="margin:12px 0;display:flex;align-items:center;gap:12px"><hr style="flex:1;border:none;border-top:1px solid var(--border);margin:0" /><span class="text-xs text-muted" style="text-transform:uppercase;letter-spacing:0.05em">or</span><hr style="flex:1;border:none;border-top:1px solid var(--border);margin:0" /></div>
        <div class="form-group"><label>Custom Domain</label><input type="url" name="domain" placeholder="https://yourdomain.com" value="${attr(pre.domain)}" autocomplete="one-time-code" data-1p-ignore data-lpignore="true" /><p class="text-xs text-muted" style="margin-top:4px">Your blog will be served from this domain instead of app.heistbrain.com.<br><a href="#help?tab=connect&connect=connect-website&section=s-custom-domain" style="color:var(--primary);text-decoration:none;font-size:11px;margin-top:2px;display:inline-block">How to set up a custom domain &rarr;</a></p></div>
        <div class="form-group"><label>Blog Path (Optional)</label><input type="text" name="blog_path" placeholder="/blog" value="${attr(blogPath)}" autocomplete="one-time-code" data-1p-ignore data-lpignore="true" /></div>
      `;
    }
    return "";
  }

  // ── Phase 31.5B: Website/Blog tracked-site rendering + handlers ──────
  _renderSiteAnalyticsIntegrations() {
    const sites = this.state.trackedSites || [];
    const sitesByPlatform = { website: [], blog: [] };
    for (const s of sites) (sitesByPlatform[s.platform] || []).push(s);

    const detect = this.state.siteDetectState;
    const added = this.state.siteAddedDetails;

    let html = `
      <h3 class="section-title" style="margin-top:32px;">Website &amp; Blog Analytics</h3>
      <p class="section-subtitle" style="margin-bottom:16px;">Heist's first-party analytics. No cookies, no third-party tracking. Your data stays in Heist.</p>
    `;

    // Premium stroke SVGs replacing the 🌐/📝 emoji. Match the stroke style
     // used elsewhere in the app (1.75 stroke, round caps/joins, currentColor).
    const WEBSITE_ICON = `<svg class="integration-site-card__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`;
    const BLOG_ICON = `<svg class="integration-site-card__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>`;

    for (const platform of ['website', 'blog']) {
      const icon = platform === 'website' ? WEBSITE_ICON : BLOG_ICON;
      const label = platform === 'website' ? 'Website' : 'Blog';
      const existing = sitesByPlatform[platform];
      html += `
        <div class="integration-site-card" data-testid="${platform}-analytics-card">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
            <div>
              <h3 class="t-h3" style="margin:0;display:inline-flex;align-items:center;gap:8px;">${icon}${label}</h3>
              <p style="margin:2px 0 0;font-size:12px;color:var(--text-muted);">
                Track pageviews on your ${platform === 'website' ? 'website' : 'blog'}
              </p>
            </div>
          </div>
          ${existing.length ? existing.map(s => this._renderTrackedSiteRow(s)).join('') : ''}
          <div class="integration-site-url-input">
            <input type="url"
                   placeholder="https://${platform === 'website' ? 'yoursite' : 'yourblog'}.com"
                   data-testid="${platform}-url-input"
                   data-site-url-input="${platform}"
                   value="${this.state.siteAddInputs?.[platform] || ''}" />
            <button class="btn btn-secondary btn-sm"
                    data-action="site-detect"
                    data-platform="${platform}"
                    data-testid="${platform}-add-btn">Add</button>
          </div>
        </div>
      `;
    }

    if (detect) html += this._renderSiteDetectModal(detect);
    if (added) html += this._renderSiteAddedModal(added);

    return html;
  }

  _renderTrackedSiteRow(s) {
    const verified = !!s.verified_at;
    const mode = s.tracking_mode === 'pixel' ? 'Pixel' : 'URL Monitoring';
    return `
      <div class="tracked-site-row" data-testid="tracked-site-row" data-site-id="${s.id}" style="display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-top:1px solid var(--border);margin-top:10px;">
        <div>
          <div style="font-weight:600;font-size:13px;">${this._escSite(s.site_name || s.site_url)} · ${mode}</div>
          <div class="${verified ? 'site-card-verified' : 'site-card-unverified'}">
            ${verified ? '✓ Verified' : 'Awaiting first hit'}
          </div>
        </div>
        <div>
          <button class="btn btn-secondary btn-sm" data-action="site-remove" data-site-id="${s.id}" data-testid="site-remove-btn">Remove</button>
        </div>
      </div>
    `;
  }

  _renderSiteDetectModal(state) {
    if (state.loading) {
      return `
        <div class="g-modal-overlay" data-testid="site-detect-modal">
          <div class="g-modal site-detect-modal">
            <h3>Detecting ${this._escSite(state.url)}…</h3>
            <p class="text-xs text-muted">Checking what's installed on your site.</p>
          </div>
        </div>
      `;
    }
    const r = state.result || {};
    const d = r.detected || {};
    const detectedList = [
      d.is_wordpress && 'WordPress',
      d.has_ga4 && 'Google Analytics 4',
      d.has_jetpack && 'Jetpack',
      d.on_cloudflare && 'Cloudflare',
      d.is_ghost && 'Ghost',
      d.is_substack && 'Substack',
      d.framework && `Framework: ${d.framework}`
    ].filter(Boolean);

    return `
      <div class="g-modal-overlay" data-testid="site-detect-modal">
        <div class="g-modal site-detect-modal">
          <h3 class="t-h3" style="margin:0 0 12px;">Setup Analytics for ${this._escSite(state.url)}</h3>
          ${detectedList.length ? `
            <div style="margin-bottom:16px;">
              <div class="t-eyebrow">We detected</div>
              <ul style="margin:6px 0 0;padding-left:18px;font-size:13px;">
                ${detectedList.map(x => `<li>${this._escSite(x)}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
          ${r.error ? `<p class="text-xs" style="color:var(--warning)">${this._escSite(r.error)}</p>` : ''}

          <div class="site-detect-recommended" data-testid="site-detect-recommended">
            <span class="site-detect-recommended-badge">Recommended</span>
            <h4 style="margin:6px 0 4px;">Heist Pixel</h4>
            <p style="margin:0;font-size:12px;color:var(--text-muted);">First-party, no cookies, full data. 2 min install — paste a script tag.</p>
            <button class="btn btn-primary btn-sm" style="margin-top:8px;" data-action="site-use-pixel" data-platform="${state.platform}" data-testid="pixel-use-btn">Use Pixel</button>
          </div>

          <div class="site-detect-alt">
            <h4 style="margin:0 0 4px;">URL Monitoring</h4>
            <p style="margin:0;font-size:12px;color:var(--text-muted);">No install — limited data (uptime, response time, post cadence).</p>
            <button class="btn btn-secondary btn-sm" style="margin-top:8px;" data-action="site-use-monitor" data-platform="${state.platform}" data-testid="monitor-use-btn">Use Monitoring</button>
          </div>

          <div style="margin-top:12px;font-size:11px;color:var(--text-muted);">
            <strong>Coming soon for WordPress sites:</strong>
            <ul style="margin:4px 0 0;padding-left:16px;">
              <li>Heist WordPress plugin (one-click install)</li>
              <li>Jetpack Stats integration</li>
              <li>Google Analytics 4 OAuth</li>
            </ul>
          </div>

          <div style="margin-top:16px;text-align:right;">
            <button class="btn btn-ghost btn-sm" data-action="site-detect-cancel" data-testid="site-detect-cancel">Cancel</button>
          </div>
        </div>
      </div>
    `;
  }

  _renderSiteAddedModal({ site, pixel_snippet }) {
    if (!site) return '';
    if (site.tracking_mode !== 'pixel' || !pixel_snippet) {
      // Monitor mode — brief confirmation then close
      return `
        <div class="g-modal-overlay" data-testid="site-added-modal">
          <div class="g-modal site-detect-modal">
            <h3>✓ URL monitoring active for ${this._escSite(site.site_name || site.site_url)}</h3>
            <p class="text-xs text-muted">You'll see data in Analytics after your first monitoring cycle (up to 24 hours).</p>
            <div style="margin-top:16px;text-align:right;">
              <button class="btn btn-primary btn-sm" data-action="site-added-close">Close</button>
            </div>
          </div>
        </div>
      `;
    }
    const disclosure = 'This site uses Heist first-party analytics (heistbrain.com) to count pageviews. No cookies or personal identifiers are used.';
    return `
      <div class="g-modal-overlay" data-testid="site-added-modal">
        <div class="g-modal site-detect-modal">
          <h3 class="t-h3" style="margin:0 0 8px;">Paste this into your site's <code>&lt;head&gt;</code></h3>
          <div class="pixel-snippet-block" data-testid="pixel-snippet">${this._escSite(pixel_snippet)}
            <button class="pixel-snippet-copy" data-action="site-copy-snippet" data-testid="pixel-copy-btn">Copy</button>
          </div>
          <p class="t-eyebrow" style="margin:12px 0 4px;">Privacy disclosure for your privacy policy</p>
          <div class="privacy-disclosure-block">${this._escSite(disclosure)}</div>
          <p style="margin-top:12px;font-size:12px;color:var(--text-muted);">
            This script is 1.8KB, runs entirely first-party, and doesn't use cookies or personal identifiers. Your visitors don't see it and can't be tracked across sites.
          </p>
          <div style="margin-top:16px;text-align:right;">
            <button class="btn btn-primary btn-sm" data-action="site-added-close" data-testid="site-added-close">Done</button>
          </div>
        </div>
      </div>
    `;
  }

  _escSite(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  async _handleSiteDetect(platform) {
    const input = this.el.querySelector(`[data-site-url-input="${platform}"]`);
    const url = (input?.value || '').trim();
    if (!url) { toast('error', 'Enter a URL first'); return; }

    this.setState({
      siteAddInputs: { ...this.state.siteAddInputs, [platform]: url },
      siteDetectState: { platform, url, loading: true, result: null }
    });
    try {
      const result = await client.integrationsSiteDetect(url);
      this.setState({ siteDetectState: { platform, url: result.url || url, loading: false, result } });
    } catch (err) {
      this.setState({ siteDetectState: { platform, url, loading: false, result: { ok: false, error: err.message, available_paths: ['url_monitor'] } } });
    }
  }

  async _handleSiteAdd(platform, mode) {
    const detect = this.state.siteDetectState;
    const url = detect?.url || this.state.siteAddInputs?.[platform] || '';
    if (!url) { toast('error', 'Detect a site first'); return; }
    try {
      const res = await client.integrationsSiteAdd({
        url,
        platform,
        tracking_mode: mode,
        site_name: null,
        detection_signals: detect?.result?.detected || null
      });
      if (!res?.ok) throw new Error(res?.error || 'Failed to add site');
      this.setState({
        siteDetectState: null,
        siteAddedDetails: { site: res.site, pixel_snippet: res.pixel_snippet }
      });
      await this.fetchTrackedSites();
    } catch (err) {
      toast('error', 'Add failed: ' + err.message);
    }
  }

  attachListeners() {
    if (!this.el) return;

    // Close help-chip popover on any click outside the popover or chip.
    document.addEventListener('click', (e) => {
      if (!this.state.helpChipOpen) return;
      if (e.target.closest('.h-help-popover')) return;
      if (e.target.closest('[data-help-chip]')) return;
      this.state.helpChipOpen = null;
      this.render();
    });

    // Coachmark tour drives the active section as it walks — so the user
    // sees the page being described, not just a highlighted nav button.
    window.addEventListener('heist:tour-activate-section', (e) => {
      const id = e?.detail?.sectionId;
      if (!id) return;
      if (this.state.activeSection === id) return;
      this.state.activeSection = id;
      // Mirror the existing nav-click side effects so each section's data
      // loads when the tour exposes it.
      if (id === 'section-approvals') this.loadApprovals();
      if (id === 'section-activity') this.loadActivity();
      this.render();
    });

    this.el.addEventListener("click", async (e) => {
      const btn = e.target.closest("button");
      if (!btn) return;

      const action = btn.dataset.action;
      const provider = btn.dataset.provider;

      // ⓘ help chip — toggle the popover for that section.
      if (btn.dataset.helpChip) {
        e.stopPropagation();
        const id = btn.dataset.helpChip;
        this.state.helpChipOpen = this.state.helpChipOpen === id ? null : id;
        this.render();
        return;
      }

      // Mobile nav sheet trigger
      if (action === 'toggle-mobile-nav') {
        this.state.mobileNavOpen = !this.state.mobileNavOpen;
        this.render();
        return;
      }

      // Agent Inbox primer CTAs — both navigate out to the full pages.
      if (action === 'agent-inbox-open') {
        window.location.hash = '#agent-inbox';
        return;
      }
      if (action === 'agent-inbox-open-help') {
        window.location.hash = '#help?tab=mcp';
        return;
      }

      // "Take a tour of Settings" — runs the settings playlist on top of
      // the existing coachmark overlay (separate from the first-run tour).
      // Pre-expand every nav group so steps 4-6 (which target nav buttons
      // inside the Content / Account / Privacy groups) anchor on visible
      // elements; otherwise their parent .settings-nav-group-items is
      // display:none and the spotlight gets a 0×0 rect → tooltip stays
      // hidden for the 8-second waitForRect window and the tour appears
      // to freeze.
      if (btn.id === 'settings-take-tour') {
        this.state.expandedGroups = new Set(['connections', 'content', 'account', 'privacy']);
        this.render();
        // setTimeout (not rAF) — the rAF callback never fires reliably
        // when this click handler also triggers a full innerHTML rebuild
        // in the same task; the timeout drops to the next macrotask after
        // the DOM has settled.
        setTimeout(() => startCoachmarkTour('settings', { startAt: 0 }), 0);
        return;
      }

      // Billing cycle toggle
      if (btn.dataset.billingCycle) {
        this.state.billingCycle = btn.dataset.billingCycle;
        this.render();
        return;
      }

      if (action === "retry") {
        this.fetchIntegrations();
      // ── Phase 31.5B: Website/Blog tracked-site actions ──
      } else if (action === "site-detect") {
        await this._handleSiteDetect(btn.dataset.platform);
      } else if (action === "site-use-pixel") {
        await this._handleSiteAdd(btn.dataset.platform, 'pixel');
      } else if (action === "site-use-monitor") {
        await this._handleSiteAdd(btn.dataset.platform, 'url_monitor');
      } else if (action === "site-detect-cancel") {
        this.setState({ siteDetectState: null });
      } else if (action === "site-added-close") {
        this.setState({ siteAddedDetails: null });
      } else if (action === "site-remove") {
        const ok = await confirmModal({ title: 'Remove tracked site', message: 'Stop collecting analytics for this site? History will be kept but no new data will record.', confirmLabel: 'Remove', danger: true });
        if (ok) {
          try {
            await client.integrationsSiteDelete(btn.dataset.siteId);
            await this.fetchTrackedSites();
          } catch (err) { toast('error', 'Remove failed: ' + err.message); }
        }
      } else if (action === "site-copy-snippet") {
        const snippet = this.state.siteAddedDetails?.pixel_snippet || '';
        try {
          await navigator.clipboard.writeText(snippet);
          toast('success', 'Snippet copied');
        } catch (_) {
          toast('success', 'Snippet ready — select and copy');
        }
      } else if (action === "linkedin-toggle-target") {
        this.setState({ linkedInTargetOpen: !this.state.linkedInTargetOpen });
      } else if (action === "connect" || action === "edit") {
        // Phase 36B: on Edit, fetch saved non-secret values + secret-set flags
        // up front so the form can pre-fill instead of rendering empty inputs.
        // Connect flow doesn't need hints (no prior values to pre-fill).
        if (action === "edit") {
          try {
            const res = await client.getIntegrationEditHints(provider);
            if (res?.ok) {
              this.setState({
                editHints: { ...this.state.editHints, [provider]: { non_secret: res.non_secret || {}, secret_set: res.secret_set || {} } }
              });
            }
          } catch (err) {
            console.warn('[SETTINGS] edit hints fetch failed:', err.message);
          }
        }
        this.setState({ connectingProvider: provider, message: null });
      } else if (action === "cancel-connect") {
        this.setState({ connectingProvider: null });
      } else if (action === "disconnect") {
        const ok = await confirmModal({ title: 'Disconnect Integration', message: `Disconnect ${provider}? This will stop analytics collection.`, confirmLabel: 'Disconnect', danger: true });
        if (ok) this.handleDisconnect(provider);
      } else if (action === "test") {
        this.handleTest(provider);
      } else if (action === "connect-meta-oauth") {
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        try {
          var res = await client.getMetaOAuthUrl();
          if (res.url) window.location.href = res.url;
          else { btn.disabled = false; btn.textContent = 'Not configured'; toast('error', 'Meta OAuth not configured. Set META_APP_ID env var.'); }
        } catch(e) { btn.disabled = false; btn.textContent = 'Error'; toast('error', 'Meta OAuth: ' + (e.message || 'Not configured')); }
      } else if (action === "connect-linkedin-oauth") {
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        try {
          var res = await client.getLinkedInOAuthUrl();
          if (res.url) window.location.href = res.url;
          else { btn.disabled = false; btn.textContent = 'Not configured'; toast('error', 'LinkedIn OAuth not configured. Set LINKEDIN_CLIENT_ID env var.'); }
        } catch(e) { btn.disabled = false; btn.textContent = 'Error'; toast('error', 'LinkedIn OAuth: ' + (e.message || 'Not configured')); }
      } else if (action === "connect-tiktok-oauth") {
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        try {
          var res = await client.connectTikTok();
          if (res.url) window.location.href = res.url;
          else { btn.disabled = false; btn.textContent = 'Not configured'; toast('error', 'TikTok OAuth not configured. Set TIKTOK_CLIENT_KEY env var.'); }
        } catch(e) { btn.disabled = false; btn.textContent = 'Error'; toast('error', 'TikTok OAuth: ' + (e.message || 'Not configured')); }
      } else if (action === "connect-youtube-oauth") {
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        try {
          var res = await client.connectYouTube();
          if (res.url) window.location.href = res.url;
          else { btn.disabled = false; btn.textContent = 'Not configured'; toast('error', 'YouTube OAuth not configured. Set YOUTUBE_CLIENT_ID env var.'); }
        } catch(e) { btn.disabled = false; btn.textContent = 'Error'; toast('error', 'YouTube OAuth: ' + (e.message || 'Not configured')); }
      } else if (action === "connect-pinterest-oauth") {
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        try {
          var res = await client.connectPinterest();
          if (res.url) window.location.href = res.url;
          else { btn.disabled = false; btn.textContent = 'Not configured'; toast('error', 'Pinterest OAuth not configured. Set PINTEREST_CLIENT_ID env var.'); }
        } catch(e) { btn.disabled = false; btn.textContent = 'Error'; toast('error', 'Pinterest OAuth: ' + (e.message || 'Not configured')); }
      } else if (action === "connect-reddit-oauth") {
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        try {
          var res = await client.connectReddit();
          if (res.url) window.location.href = res.url;
          else { btn.disabled = false; btn.textContent = 'Not configured'; toast('error', 'Reddit OAuth not configured. Set REDDIT_CLIENT_ID env var.'); }
        } catch(e) { btn.disabled = false; btn.textContent = 'Error'; toast('error', 'Reddit OAuth: ' + (e.message || 'Not configured')); }
      } else if (action === "connect-wordpress-oauth") {
        const blogUrl = prompt('Enter your WordPress.com site URL (e.g. mysite.wordpress.com):');
        if (!blogUrl) return;
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        try {
          var res = await client.connectWordPressCom(blogUrl.trim());
          if (res.url) window.location.href = res.url;
          else { btn.disabled = false; btn.textContent = 'Not configured'; toast('error', 'WordPress.com OAuth not configured.'); }
        } catch(e) { btn.disabled = false; btn.textContent = 'Error'; toast('error', 'WordPress.com OAuth: ' + (e.message || 'Not configured')); }
      } else if (action === "connect-google-docs") {
        try {
          const res = await client.googleDocsAuth();
          if (res.url) window.location.href = res.url;
          else toast('error', 'Google Docs integration is not available yet. Contact support.');
        } catch(e) { toast('error', 'Google Docs: ' + (e.message || 'Not configured')); }
      } else if (action === "pick-google-docs") {
        openDocPicker('google_docs', { onComplete: () => { toast('success', 'Documents synced to Brain!'); this.render(); } });
      } else if (action === "disconnect-google-docs") {
        if (confirm('Disconnect Google Docs? Synced documents will be removed from your Brain.')) {
          try { await client.googleDocsDisconnect(); this._googleConnected = false; this.render(); toast('success', 'Google Docs disconnected'); } catch(e) { toast('error', e.message); }
        }
      } else if (action === "connect-notion") {
        try {
          const res = await client.notionAuth();
          if (res.url) window.location.href = res.url;
          else toast('error', 'Notion integration is not available yet. Contact support.');
        } catch(e) { toast('error', 'Notion: ' + (e.message || 'Not configured')); }
      } else if (action === "pick-notion-pages") {
        openDocPicker('notion', { onComplete: () => { toast('success', 'Pages synced to Brain!'); this.render(); } });
      } else if (action === "disconnect-notion") {
        if (confirm('Disconnect Notion? Synced pages will be removed from your Brain.')) {
          try { await client.notionDisconnect(); this._notionConnected = false; this.render(); toast('success', 'Notion disconnected'); } catch(e) { toast('error', e.message); }
        }
      } else if (action === "ai-retry") {
        this.fetchAiSettings();
      } else if (action === "ai-save") {
        this.handleAiSave();
      } else if (action === "ai-clear") {
        const ok = await confirmModal({ title: 'Delete API Key', message: 'Are you sure you want to delete your saved OpenAI key? This cannot be undone.', confirmLabel: 'Delete', danger: true });
        if (ok) this.handleAiClear();
      } else if (action === "delete-rule") {
        const ruleId = btn.dataset.ruleId;
        if (ruleId) this.handleDeleteRule(ruleId);
      } else if (btn.id === "save-rule-btn") {
        this.handleSaveRule();
      } else if (action === 'account-save') {
        await this.handleAccountSave();
      } else if (action === 'password-save') {
        await this.handlePasswordSave();
      } else if (action === 'brand-save') {
        await this.handleBrandSave();
      } else if (action === 'upload-avatar') {
        const fileInput = this.el.querySelector('#avatar-file-input');
        if (fileInput) fileInput.click();
      } else if (action === 'upgrade') {
        const tier = btn.dataset.tier;
        const cycle = btn.dataset.cycle || this.state.billingCycle;
        btn.disabled = true;
        btn.textContent = 'Redirecting...';
        try {
          const res = await client.createCheckout(tier, cycle);
          if (res.url) window.location.href = res.url;
          else { btn.disabled = false; btn.textContent = 'Try again'; }
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Error - try again';
        }
      } else if (action === 'open-portal') {
        btn.disabled = true;
        try {
          const res = await client.createPortalSession();
          if (res.url) window.location.href = res.url;
          else btn.disabled = false;
        } catch (err) {
          btn.disabled = false;
        }
      } else if (action === 'save-stock-policy') {
        const brandId = btn.dataset.brandId;
        if (!brandId) return;
        const parseTerms = (id) => (this.container.querySelector('#' + id)?.value || '').split(',').map(s => s.trim()).filter(Boolean);
        const orientation = this.container.querySelector('#stock-orientation')?.value || null;
        const patch = {
          preferredTerms: parseTerms('stock-preferred'),
          bannedTerms: parseTerms('stock-banned'),
          avoidPeople: !!this.container.querySelector('#stock-avoid-people')?.checked,
          sources: {
            pexels: !!this.container.querySelector('#stock-src-pexels')?.checked,
            unsplash: !!this.container.querySelector('#stock-src-unsplash')?.checked,
          },
          orientation: orientation || null,
        };
        btn.disabled = true; btn.textContent = 'Saving…';
        try {
          const res = await client.updateStockPolicy(brandId, patch);
          this.setState({ stockPolicy: res.policy || patch });
          if (typeof toast === 'function') toast('success', 'Stock curation rules saved');
        } catch (e) { if (typeof toast === 'function') toast('error', e.message || 'Save failed'); btn.disabled = false; btn.textContent = 'Save curation rules'; }
      } else if (action === 'unblock-stock') {
        const brandId = btn.dataset.brandId; const id = btn.dataset.blockId;
        try {
          const res = await client.unblockStockMedia(brandId, id);
          this.setState({ stockBlocklist: res.blocklist || [] });
        } catch (e) { if (typeof toast === 'function') toast('error', e.message || 'Unblock failed'); }
      } else if (action === 'new-brand') {
        import('../ui/brandWizard.js').then(m => {
          m.showBrandWizard({ onComplete: () => this.loadBrands() });
        });
      } else if (action === 'switch-brand') {
        const brandId = btn.dataset.brandId;
        try {
          await client.switchBrand(brandId);
          localStorage.setItem('heist_active_brand', brandId);
          window.location.reload();
        } catch (e) { console.error('[BRANDS] Switch error:', e); toast('error', 'Couldn\'t switch brands — please try again.'); }
      } else if (action === 'toggle-pause') {
        const brandId = btn.dataset.brandId;
        try {
          const res = await client.togglePauseBrand(brandId);
          if (res.ok || res.publishing_paused !== undefined) {
            this.loadBrands();
          }
        } catch (e) { console.error('[BRANDS] Pause toggle error:', e); toast('error', 'Couldn\'t update publishing for that brand — please try again.'); }
      } else if (action === 'rename-brand') {
        const brandId = btn.dataset.brandId;
        const currentName = btn.dataset.brandName;
        const newName = prompt('Rename brand:', currentName);
        if (newName && newName.trim() && newName.trim() !== currentName) {
          try { await client.renameBrand(brandId, newName.trim()); this.loadBrands(); }
          catch (e) { alert(e.message || 'Failed to rename brand'); }
        }
      } else if (action === 'delete-brand') {
        const brandId = btn.dataset.brandId;
        const brandName = btn.dataset.brandName;
        if (!confirm(`Delete "${brandName}"? This will permanently remove all content, campaigns, Brain data, and chat history for this brand. This cannot be undone.`)) return;
        try { await client.deleteBrand(brandId); this.loadBrands(); }
        catch (e) { alert(e.message || 'Failed to delete brand'); }
      } else if (action === 'share-brand') {
        const label = prompt('Label for this share link (optional):') || '';
        const expiryChoice = prompt('Expires in how many days? (7, 30, 90, or leave blank for no expiry):');
        const expiresInDays = expiryChoice && [7, 30, 90].includes(parseInt(expiryChoice)) ? parseInt(expiryChoice) : null;
        try {
          const res = await client.createBrandShare({ label: label.trim() || null, expiresInDays });
          if (res.url) {
            try { await navigator.clipboard.writeText(res.url); } catch(e) {}
            alert('Share link created and copied to clipboard!\n\n' + res.url);
          }
          this.loadBrandShares();
          this.render();
        } catch (e) { alert(e.message || 'Failed to create share link'); }
      } else if (action === 'copy-share-link') {
        try { await navigator.clipboard.writeText(btn.dataset.url); btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy'; }, 2000); }
        catch(e) { alert('Copy failed. URL: ' + btn.dataset.url); }
      } else if (action === 'revoke-share') {
        if (!confirm('Revoke this share link? Clients will no longer be able to access it.')) return;
        try { await client.revokeBrandShare(btn.dataset.shareId); this.loadBrandShares(); this.render(); }
        catch(e) { alert(e.message || 'Failed to revoke'); }
      } else if (action === 'create-workspace') {
        btn.disabled = true;
        btn.textContent = 'Creating...';
        try {
          await client.createWorkspace('My Agency');
          await this.loadWorkspace();
          await this.loadBrands();
          this.render();
        } catch (e) { btn.disabled = false; btn.textContent = 'Create Workspace'; alert(e.message || 'Failed to create workspace'); }
      } else if (action === 'save-agency-brain') {
        const msgEl = this.el.querySelector('#agency-brain-msg');
        try {
          await client.updateAgencyBrain({
            mission: this.el.querySelector('#ab-mission')?.value || '',
            audience: this.el.querySelector('#ab-audience')?.value || '',
            voice: this.el.querySelector('#ab-voice')?.value || '',
            keywords: this.el.querySelector('#ab-keywords')?.value || '',
            avoid_words: this.el.querySelector('#ab-avoid')?.value || ''
          });
          if (msgEl) { msgEl.style.color = 'var(--success)'; msgEl.textContent = 'Saved!'; setTimeout(() => { msgEl.textContent = ''; }, 2000); }
        } catch (e) { if (msgEl) { msgEl.style.color = 'var(--danger)'; msgEl.textContent = e.message || 'Failed to save'; } }
      } else if (action === 'invite-member') {
        const email = this.el.querySelector('#invite-email')?.value;
        const role = this.el.querySelector('#invite-role')?.value;
        const msgEl = this.el.querySelector('#invite-msg');
        if (!email?.trim()) { if (msgEl) { msgEl.style.color = 'var(--danger)'; msgEl.textContent = 'Email required'; } return; }
        try {
          await client.inviteMember({ email, role, brandIds: null });
          if (msgEl) { msgEl.style.color = 'var(--success)'; msgEl.textContent = 'Invitation sent!'; }
          const emailInput = this.el.querySelector('#invite-email'); if (emailInput) emailInput.value = '';
          await this.loadWorkspace();
          this.render();
        } catch (e) { if (msgEl) { msgEl.style.color = 'var(--danger)'; msgEl.textContent = e.message || 'Failed to invite'; } }
      } else if (action === 'reset-member-pwd') {
        const userId = btn.dataset.userId;
        const name = btn.dataset.name;
        const newPwd = prompt(`Enter new password for ${name} (min 8 chars):`);
        if (!newPwd || newPwd.length < 8) { if (newPwd !== null) alert('Password must be at least 8 characters'); return; }
        try { await client.resetMemberPassword(userId, newPwd); alert('Password reset successfully'); }
        catch (e) { alert(e.message || 'Failed to reset password'); }
      } else if (action === 'remove-member') {
        const userId = btn.dataset.userId;
        const name = btn.dataset.name;
        if (!confirm(`Remove ${name} from the workspace?`)) return;
        try { await client.removeMember(userId); await this.loadWorkspace(); this.render(); }
        catch (e) { alert(e.message || 'Failed to remove member'); }
      } else if (action === 'add-seats') {
        const ws = this.state.workspace;
        const maxAdd = 50 - (ws?.seatLimit || 10);
        const input = prompt(`How many seats to add? (1-${maxAdd}, $10/seat/mo)`);
        if (!input) return;
        const count = parseInt(input);
        if (!count || count < 1 || count > maxAdd) {
            alert(`Please enter a number between 1 and ${maxAdd}`);
            return;
        }
        try {
            const res = await client.addSeats(count);
            if (res.url) window.location.href = res.url;
        } catch (e) {
            alert(e.message || 'Failed to start seat purchase');
        }
        return;
      } else if (action === 'revoke-invite') {
        const inviteId = btn.dataset.inviteId;
        try { await client.revokeInvite(inviteId); await this.loadWorkspace(); this.render(); }
        catch (e) { alert(e.message || 'Failed to revoke invite'); }
      } else if (action === 'upload-logo' || action === 'remove-logo') {
        // File upload: deferred — no server route exists yet
      } else if (btn.classList.contains('settings-nav-group-heading')) {
        const group = btn.dataset.group;
        const groupEl = btn.closest('.settings-nav-group');
        if (this.state.expandedGroups.has(group)) {
          this.state.expandedGroups.delete(group);
          groupEl.classList.remove('expanded');
        } else {
          this.state.expandedGroups.add(group);
          groupEl.classList.add('expanded');
        }
      } else if (btn.classList.contains('settings-nav-item') || btn.classList.contains('settings-tab') || btn.classList.contains('settings-mobile-nav__item')) {
        // Selecting an item closes the mobile sheet.
        this.state.mobileNavOpen = false;
        // "nav:" prefix → route away from settings instead of switching an internal section
        if (btn.dataset.section && btn.dataset.section.startsWith('nav:')) {
          window.location.hash = btn.dataset.section.slice('nav:'.length);
          return;
        }
        this.state.activeSection = btn.dataset.section;
        if (btn.dataset.section === 'section-approvals') this.loadApprovals();
        if (btn.dataset.section === 'section-activity') this.loadActivity();
        // Auto-expand the parent group when a sub-tab is clicked
        const groupMap = { 'section-integrations':'connections','section-ai-settings':'connections','schedule-rules-section':'content','section-brands':'content','section-account':'account','section-billing':'account','section-team-management':'account','section-approvals':'account','section-activity':'account','section-team':'privacy' };
        if (groupMap[btn.dataset.section]) this.state.expandedGroups.add(groupMap[btn.dataset.section]);
        this.render();
      } else if (action === 'approval-filter') {
        this.state.approvalFilter = btn.dataset.filter;
        // Clear bulk selection when changing tab — selections from a different
        // filter won't be visible anyway and could trigger surprising bulk ops.
        this.state.approvalSelectedIds.clear();
        this.loadApprovals();
      } else if (action === 'approval-toggle-select') {
        const id = btn.dataset.id;
        if (this.state.approvalSelectedIds.has(id)) this.state.approvalSelectedIds.delete(id);
        else this.state.approvalSelectedIds.add(id);
        this.render();
      } else if (action === 'approval-select-all') {
        const visiblePendingIds = this.state.approvals.filter(a => a.status === 'pending').map(a => a.id);
        const allSelected = visiblePendingIds.length > 0 && visiblePendingIds.every(id => this.state.approvalSelectedIds.has(id));
        if (allSelected) visiblePendingIds.forEach(id => this.state.approvalSelectedIds.delete(id));
        else visiblePendingIds.forEach(id => this.state.approvalSelectedIds.add(id));
        this.render();
      } else if (action === 'approval-clear-selection') {
        this.state.approvalSelectedIds.clear();
        this.render();
      } else if (action === 'approve-selected') {
        const ids = Array.from(this.state.approvalSelectedIds);
        if (!ids.length) return;
        const ok = await confirmModal({
          title: `Approve ${ids.length} ${ids.length === 1 ? 'item' : 'items'}?`,
          message: 'They will move to "approved" status and become schedulable. The writers will be notified.',
          confirmLabel: 'Approve all',
        });
        if (!ok) return;
        const note = await promptModal({
          title: 'Add a shared note (optional)',
          message: 'This note attaches to every approval in the batch.',
          defaultValue: '',
          confirmLabel: 'Approve all',
        });
        if (note === null) return;
        let okCount = 0, failCount = 0;
        for (const id of ids) {
          try { await client.approveContent(id, note || undefined); okCount++; }
          catch (_) { failCount++; }
        }
        this.state.approvalSelectedIds.clear();
        if (failCount) toast('warn', `Approved ${okCount} of ${ids.length}. ${failCount} failed.`);
        else toast('success', `Approved ${okCount} ${okCount === 1 ? 'item' : 'items'}.`);
        await this.loadApprovals();
      } else if (action === 'reject-selected') {
        const ids = Array.from(this.state.approvalSelectedIds);
        if (!ids.length) return;
        const note = await promptModal({
          title: `Reject ${ids.length} ${ids.length === 1 ? 'item' : 'items'}?`,
          message: 'Tell the writers what needs to change. The note attaches to every rejection in the batch and is required.',
          defaultValue: '',
          confirmLabel: 'Reject all',
        });
        if (note === null) return;
        if (!note.trim()) { toast('warn', 'A rejection note is required.'); return; }
        let okCount = 0, failCount = 0;
        for (const id of ids) {
          try { await client.rejectContent(id, note.trim()); okCount++; }
          catch (_) { failCount++; }
        }
        this.state.approvalSelectedIds.clear();
        if (failCount) toast('warn', `Rejected ${okCount} of ${ids.length}. ${failCount} failed.`);
        else toast('success', `Rejected ${okCount} ${okCount === 1 ? 'item' : 'items'}.`);
        await this.loadApprovals();
      } else if (action === 'approve-content') {
        const id = btn.dataset.id;
        const note = await promptModal({
          title: 'Approve content',
          message: 'Add an optional note for the writer (or leave blank).',
          defaultValue: '',
          confirmLabel: 'Approve',
          cancelLabel: 'Cancel',
        });
        if (note === null) return; // user cancelled
        try { await client.approveContent(id, note || undefined); await this.loadApprovals(); }
        catch (e) { toast('error', e.message || 'Failed to approve'); }
      } else if (action === 'reject-content') {
        const id = btn.dataset.id;
        const note = await promptModal({
          title: 'Reject content',
          message: 'Tell the writer what needs to change. The note is required so they know how to revise.',
          defaultValue: '',
          confirmLabel: 'Reject',
          cancelLabel: 'Cancel',
        });
        if (note === null) return;
        if (!note.trim()) { toast('warn', 'A rejection note is required.'); return; }
        try { await client.rejectContent(id, note.trim()); await this.loadApprovals(); }
        catch (e) { toast('error', e.message || 'Failed to reject'); }
      } else if (action === 'activity-load-more') {
        this.state.activityOffset += 50;
        this.loadActivity(true);
      } else if (action === 'export-data') {
        btn.textContent = 'Exporting...';
        btn.disabled = true;
        try {
            const res = await client.exportMyData();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'heist-export.json';
            a.click();
            URL.revokeObjectURL(url);
            btn.textContent = 'Downloaded!';
            setTimeout(() => { btn.textContent = 'Download My Data'; btn.disabled = false; }, 3000);
        } catch(err) {
            this.setState({ message: { type: 'error', text: 'Export failed: ' + err.message } });
            btn.textContent = 'Download My Data';
            btn.disabled = false;
        }
      } else if (action === 'delete-account') {
        const password = prompt('Type your password to confirm account deletion:');
        if (!password) return;
        const confirmed = confirm('This will permanently delete your account and ALL data. This cannot be undone. Are you absolutely sure?');
        if (!confirmed) return;
        try {
            await client.deleteAccount(password);
            window.__auth__ = null;
            window.location.hash = '#login';
        } catch(err) {
            this.setState({ message: { type: 'error', text: 'Delete failed: ' + err.message } });
        }
      }
    });

    this.el.addEventListener("change", async (e) => {
      // AI provider model selector (per-card)
      if (e.target.dataset?.action === 'select-provider-model') {
          const provider = e.target.dataset.provider;
          const model = e.target.value;
          // Only save to server if this provider is currently active
          const currentModel = this.state.aiSettings?.current_model || 'gpt-4o-mini';
          const PROV_MODELS = { openai: ['gpt-4o-mini','gpt-4o','gpt-4.1','gpt-4.1-mini','gpt-4.1-nano','o3-mini'], anthropic: ['claude-sonnet-4-20250514','claude-haiku-4-5-20251001'], google: ['gemini-2.5-flash','gemini-2.5-pro'], openrouter: ['meta-llama/llama-3.3-70b-instruct:free','mistralai/mistral-7b-instruct:free','google/gemini-2.0-flash-exp:free','anthropic/claude-sonnet-4','openai/gpt-4o-mini'] };
          const isActive = (PROV_MODELS[provider] || []).includes(currentModel);
          if (isActive) {
            try {
              await client.updateAiModel(model);
              this.setState({
                aiSettings: { ...this.state.aiSettings, current_model: model },
                aiMessage: { type: 'success', text: `Model updated to ${model}` }
              });
            } catch (err) { this.setState({ aiError: err.message || 'Failed to update model' }); }
          }
          // If not active, just let the dropdown change locally — it'll be used when they click "Use This Provider"
          return;
      }
      // Admin: server key policy change
      if (e.target.dataset?.action === 'change-server-policy') {
          const policy = e.target.value;
          try {
            await client.updateServerKeyPolicy(policy);
            this.state.serverKeyPolicy = policy;
            this.render();
          } catch (err) { this.setState({ aiError: err.message || 'Failed to update server policy' }); }
          return;
      }
      // Activity log filters
      if (e.target.dataset?.action === 'activity-brand-filter') {
        this.state.activityBrandFilter = e.target.value;
        this.loadActivity();
      } else if (e.target.dataset?.action === 'activity-action-filter') {
        this.state.activityActionFilter = e.target.value;
        this.loadActivity();
      }
      // Team role change
      if (e.target.dataset?.action === 'change-role') {
        const userId = e.target.dataset.userId;
        const role = e.target.value;
        try { await client.updateMemberRole(userId, role); await this.loadWorkspace(); this.render(); }
        catch (err) { alert(err.message || 'Failed to change role'); }
        return;
      }
      if (e.target.id === 'avatar-file-input' && e.target.files?.length) {
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('avatar', file);
        try {
          const res = await client.uploadAvatar(formData);
          if (res.success || res.ok) {
            await this.fetchAccountData();
            if (window.__auth__ && this.state.accountData) {
              window.__auth__.avatar_path = this.state.accountData.avatar_path;
              window.updateGlobalAuthState(window.__auth__, 'AVATAR_UPLOAD');
            }
          }
        } catch (err) {
          this.setState({ accountMsg: { type: 'error', text: 'Failed to upload photo: ' + err.message } });
        }
        e.target.value = '';
      } else if (e.target.dataset.action === "change-ai-mode") {
        this.setState({ aiDraftMode: e.target.value, aiMessage: null });
      }
    });

    this.el.addEventListener("input", (e) => {
      if (e.target.dataset.action === "change-ai-key") {
        this.state.aiDraftKey = e.target.value; // mutable bind without full re-render
      }
    });

    // AI model selector
    // Provider key save/clear
    // AI provider key save/clear — use delegation so it works after re-renders
    this.el.addEventListener('click', async (e) => {
      const saveBtn = e.target.closest('[data-action="save-provider-key"]');
      if (saveBtn) {
        const prov = saveBtn.dataset.provider;
        const input = this.el.querySelector(`input[data-provider-key="${prov}"]`);
        const key = input?.value?.trim();
        if (!key) { toast('error', 'Enter an API key first'); return; }
        // Format validation
        const keyPatterns = { openai: /^sk-/, anthropic: /^sk-ant-/, google: /^AIza/, replicate: /^r8_/, openrouter: /^sk-or-/, elevenlabs: /^sk_/, creatomate: /.{20,}/ };
        if (keyPatterns[prov] && !keyPatterns[prov].test(key)) {
          const provNames = { openai: 'OpenAI', anthropic: 'Anthropic', google: 'Google AI', replicate: 'Replicate', openrouter: 'OpenRouter', elevenlabs: 'ElevenLabs', creatomate: 'Creatomate' };
          toast('error', `Invalid ${provNames[prov] || prov} key format. Check your key and try again.`);
          return;
        }
        saveBtn.disabled = true; saveBtn.textContent = 'Testing…';
        try {
          await client.updateAiSettings({ mode: this.state.aiDraftMode || this.state.aiSettings?.mode || 'byok', provider: prov, api_key: key });
          input.value = '';
          const res = await client.getAiSettings();
          const provData = res.item?.providers?.[prov];
          if (provData?.has_key) {
            this.setState({ aiSettings: res.item, aiError: null, aiMessage: { type: 'success', text: `✓ Connected to ${prov.charAt(0).toUpperCase() + prov.slice(1)} — key tested and saved.` } });
          } else {
            this.setState({ aiSettings: res.item, aiError: `Key was saved but could not be verified. Check that the key is valid.` });
          }
        } catch (err) {
          saveBtn.disabled = false; saveBtn.textContent = 'Save';
          console.error('[AI Settings] Save key error:', err.message, err.code || '');
          // Server now live-pings the provider before saving. On failure the
          // error.message is already the user-facing copy from the server,
          // so surface it directly instead of the generic fallback.
          const msg = err.code === 'BYOK_TEST_FAILED'
            ? err.message
            : err.code === 'NETWORK_ERROR'
              ? "Couldn't reach the server. Check your connection and try again."
              : (err.message || 'Failed to save your API key. Please check the key is valid and try again.');
          this.setState({ aiError: msg, aiMessage: null });
        }
        return;
      }
      const eyeBtn = e.target.closest('[data-action="toggle-key-visibility"]');
      if (eyeBtn) {
        const prov = eyeBtn.dataset.provider;
        const input = this.el.querySelector(`input[data-provider-key="${prov}"]`);
        if (input) {
          const isPassword = input.type === 'password';
          input.type = isPassword ? 'text' : 'password';
          eyeBtn.querySelector('.eye-open').style.display = isPassword ? 'none' : '';
          eyeBtn.querySelector('.eye-closed').style.display = isPassword ? '' : 'none';
        }
        return;
      }
      const wpEyeBtn = e.target.closest('[data-action="toggle-wp-password"]');
      if (wpEyeBtn) {
        const input = wpEyeBtn.closest('.ai-prov-key-wrap')?.querySelector('input');
        if (input) {
          const isPassword = input.type === 'password';
          input.type = isPassword ? 'text' : 'password';
          wpEyeBtn.querySelector('.eye-open').style.display = isPassword ? 'none' : '';
          wpEyeBtn.querySelector('.eye-closed').style.display = isPassword ? '' : 'none';
        }
        return;
      }
      const activateBtn = e.target.closest('[data-action="activate-provider"]');
      if (activateBtn) {
        const prov = activateBtn.dataset.provider;
        // Get the model selected in that provider's card
        const modelSelect = this.el.querySelector(`select[data-action="select-provider-model"][data-provider="${prov}"]`);
        const model = modelSelect?.value;
        if (model) {
          activateBtn.disabled = true; activateBtn.textContent = 'Activating...';
          try {
            await client.updateAiModel(model);
            this.setState({
              aiSettings: { ...this.state.aiSettings, current_model: model },
              aiMessage: { type: 'success', text: `${prov.charAt(0).toUpperCase() + prov.slice(1)} is now your primary AI provider.` }
            });
          } catch (err) {
            activateBtn.disabled = false; activateBtn.textContent = 'Use This Provider';
            this.setState({ aiError: err.message || 'Failed to activate provider' });
          }
        }
        return;
      }
      const clearBtn = e.target.closest('[data-action="clear-provider-key"]');
      if (clearBtn) {
        const prov = clearBtn.dataset.provider;
        clearBtn.disabled = true;
        try {
          await client.request(`/api/ai-settings/key/${prov}`, { method: 'DELETE' });
          const res = await client.getAiSettings();
          this.setState({ aiSettings: res.item, aiMessage: { type: 'success', text: `${prov.charAt(0).toUpperCase() + prov.slice(1)} key cleared.` } });
        } catch (err) {
          clearBtn.disabled = false;
          this.setState({ aiError: err.message || 'Failed to clear key' });
        }
        return;
      }

      // Brain-write approval mode — toggle between review/instant
      const brainWriteInput = e.target.closest('input[data-action="brain-write-mode"]');
      if (brainWriteInput) {
        const mode = brainWriteInput.value;
        if (mode === 'review' || mode === 'instant') {
          this.saveBrainWriteMode(mode);
        }
        return;
      }
    });


    this.el.addEventListener("submit", (e) => {
      e.preventDefault();
      const form = e.target;
      // LinkedIn post-target update (partial credential edit, no reconnect)
      if (form.classList?.contains('linkedin-target-form')) {
        const fd = new FormData(form);
        this.handleLinkedInTargetSave({
          post_targets: fd.get('post_targets') || 'personal',
          organization_id: (fd.get('organization_id') || '').toString().trim()
        });
        return;
      }
      const provider = form.dataset.provider;
      if (provider) {
        this.handleConnect(provider, new FormData(form));
      }
    });
  }

  async handleSaveRule() {
    if (!this.el) return;
    const platform = this.el.querySelector('#rule-platform')?.value;
    const time = this.el.querySelector('#rule-time')?.value || '10:00';
    const maxPerDay = parseInt(this.el.querySelector('#rule-max')?.value, 10) || 3;
    const checkedDays = Array.from(this.el.querySelectorAll('.rule-day-cb:checked')).map(cb => cb.value);

    if (checkedDays.length === 0) return;

    const cadence = checkedDays.map(day => ({ day, time }));
    try {
      await client.createScheduleRule({
        platform,
        name: platform + ' schedule',
        cadence,
        maxPostsPerDay: maxPerDay
      });
      await this.fetchScheduleRules();
      this.rerenderRulesSection();
    } catch (err) {
      console.error('[SETTINGS] Save rule failed:', err.message);
      toast('error', 'Couldn\'t save that schedule rule — please try again.');
    }
  }

  async handleDeleteRule(id) {
    try {
      await client.deleteScheduleRule(id);
      await this.fetchScheduleRules();
      this.rerenderRulesSection();
    } catch (err) {
      console.error('[SETTINGS] Delete rule failed:', err.message);
      toast('error', 'Couldn\'t delete that schedule rule — please try again.');
    }
  }

  rerenderRulesSection() {
    if (!this.el) return;
    const section = this.el.querySelector('#schedule-rules-section');
    if (section) {
      section.innerHTML = `
        <h2 class="section-title">Posting Schedule</h2>
        <p class="section-subtitle">Define posting windows for auto-fill scheduling.</p>
        ${this.renderScheduleRulesSection()}
      `;
    }
  }

  async handleConnect(provider, formData) {
    try {
      this.setState({ loading: true, message: null });

      const payload = {
        account_label: formData.get("account_label"),
        credentials: {}
      };

      // Extract credentials dynamically
      for (const [key, value] of formData.entries()) {
        if (key !== "account_label") {
          // Handle 'credentials_bearer_token' mapping if needed, or simple direct mapping
          const credKey = key === "credentials_bearer_token" ? "bearer_token" : key;
          payload.credentials[credKey] = value;
        }
      }

      await client.connectIntegration(provider, payload);

      // Success: Clear sensitive state and refresh
      this.setState({ connectingProvider: null, message: { type: "success", text: "Connected successfully." } });
      this.fetchIntegrations();

    } catch (err) {
      const msg = `${err.message}${err.requestId ? ` (RID: ${err.requestId})` : ""} `;
      this.setState({ loading: false, message: { type: "error", text: msg } });
    }
  }

  async handleLinkedInTargetSave(payload) {
    try {
      const res = await client.updateLinkedInTarget(payload);
      if (res.ok) {
        this.setState({
          linkedInTargetOpen: false,
          message: { type: 'success', text: 'LinkedIn target updated.' }
        });
        this.fetchIntegrations();
      } else {
        const msg = res.error?.message || 'Failed to update target.';
        this.setState({ message: { type: 'error', text: msg } });
      }
    } catch (err) {
      this.setState({ message: { type: 'error', text: `Update failed: ${err.message}` } });
    }
  }

  async handleDisconnect(provider) {
    // Safety: ensure confirm modal is removed (belt-and-suspenders)
    document.querySelectorAll('.g-modal-overlay').forEach(el => el.remove());
    try {
      this.setState({ loading: true, message: null });
      await client.disconnectIntegration(provider);
      this.setState({ loading: false, connectingProvider: null, message: { type: "success", text: "Disconnected." } });
      this.fetchIntegrations();
    } catch (err) {
      const msg = `${err.message}${err.requestId ? ` (RID: ${err.requestId})` : ""} `;
      this.setState({ loading: false, message: { type: "error", text: msg } });
    }
  }

  async handleTest(provider) {
    try {
      this.setState({ testingProvider: provider, message: null });
      const res = await client.testIntegration(provider);
      if (res.ok) {
        this.setState({
          testingProvider: null,
          message: { type: "success", text: res.message || "Connection verified." }
        });
      }
    } catch (err) {
      this.setState({
        testingProvider: null,
        message: { type: "error", text: `Test failed: ${err.message}${err.requestId ? ` (RID: ${err.requestId})` : ""} ` }
      });
    }
  }

  async handleAiSave() {
    try {
      this.setState({ aiLoading: true, aiMessage: null, aiError: null });
      const payload = {
        mode: this.state.aiDraftMode || 'my_plan',
        provider: 'openai',
        api_key: this.state.aiDraftKey || ""
      };
      const res = await client.updateAiSettings(payload);
      if (res.ok) {
        // Re-fetch to get updated key hints and provider states
        await this.fetchAiSettings();
        this.setState({
          aiDraftKey: "",
          aiLoading: false,
          aiMessage: { type: "success", text: "AI settings saved successfully." }
        });
      }
    } catch (err) {
      const msg = `${err.message}${err.requestId ? ` (RID: ${err.requestId})` : ""} `;
      this.setState({ aiLoading: false, aiMessage: { type: "error", text: msg } });
    }
  }

  async handleAccountSave() {
    if (!this.el) return;
    const name = this.el.querySelector('#account-name')?.value?.trim();
    const timezone = this.el.querySelector('#account-timezone')?.value;
    if (!name) {
      this.setState({ accountMsg: { type: 'error', text: 'Name cannot be empty' } });
      return;
    }
    this.setState({ accountSaving: true, accountMsg: null });
    try {
      const csrfToken = document.cookie.split('; ').find(c => c.startsWith('csrf_token='))?.split('=')[1] || '';
      const res = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: JSON.stringify({ name, timezone })
      });
      if (!res.ok) throw new Error('Save failed');
      if (this.state.accountData) {
        this.setState({
          accountSaving: false,
          accountMsg: { type: 'success', text: '✓ Saved' },
          accountData: { ...this.state.accountData, name, timezone }
        });
      } else {
        this.setState({ accountSaving: false, accountMsg: { type: 'success', text: '✓ Saved' } });
      }
      setTimeout(() => this.setState({ accountMsg: null }), 3000);
    } catch (err) {
      this.setState({ accountSaving: false, accountMsg: { type: 'error', text: err.message || 'Save failed' } });
    }
  }

  async handleBrandSave() {
    if (!this.el) return;
    const nameVal = this.el.querySelector('#brand-name')?.value?.trim();
    if (!nameVal) {
      this.setState({ brandSaveMsg: { type: 'error', text: 'Name cannot be empty' } });
      return;
    }
    // No dedicated server route for brand name yet.
    // Update local aiSettings.brandName so the sidebar initial and avatar update visually.
    if (this.state.aiSettings) {
      this.setState({
        aiSettings: { ...this.state.aiSettings, brandName: nameVal },
        brandSaveMsg: { type: 'success', text: '✓ Saved' }
      });
    } else {
      this.setState({ brandSaveMsg: { type: 'success', text: '✓ Saved' } });
    }
    setTimeout(() => this.setState({ brandSaveMsg: null }), 3000);
  }

  async handlePasswordSave() {
    if (!this.el) return;
    const currentPw = this.el.querySelector('#account-current-pw')?.value;
    const newPw = this.el.querySelector('#account-new-pw')?.value;
    const confirmPw = this.el.querySelector('#account-confirm-pw')?.value;
    if (!currentPw || !newPw || !confirmPw) {
      this.setState({ passwordMsg: { type: 'error', text: 'All fields are required' } });
      return;
    }
    if (newPw !== confirmPw) {
      this.setState({ passwordMsg: { type: 'error', text: "New passwords don't match." } });
      return;
    }
    if (newPw.length < 8) {
      this.setState({ passwordMsg: { type: 'error', text: 'Min. 8 characters' } });
      return;
    }
    this.setState({ passwordSaving: true, passwordMsg: null });
    try {
      // Get CSRF token from cookie
      const csrfToken = document.cookie.split('; ').find(c => c.startsWith('csrf_token='))?.split('=')[1] || '';
      const res = await fetch('/api/auth/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw })
      });
      const data = await res.json();
      if (!res.ok) {
        const errMsg = (typeof data.error === 'object' && data.error?.message) ? data.error.message : (typeof data.error === 'string' ? data.error : 'Password update failed');
        throw new Error(errMsg);
      }
      this.setState({ passwordSaving: false, passwordMsg: { type: 'success', text: '✓ Password updated' } });
      // Clear fields on success
      ['#account-current-pw', '#account-new-pw', '#account-confirm-pw'].forEach(sel => {
        const el = this.el.querySelector(sel);
        if (el) el.value = '';
      });
      setTimeout(() => this.setState({ passwordMsg: null }), 3000);
    } catch (err) {
      this.setState({ passwordSaving: false, passwordMsg: { type: 'error', text: err.message || 'Update failed' } });
    }
  }

  async handleAiClear() {
    try {
      this.setState({ aiLoading: true, aiMessage: null, aiError: null });
      const res = await client.clearAiSettingsKey();
      if (res.ok) {
        // optimistically update local state or re-fetch
        await this.fetchAiSettings();
        this.setState({
          aiDraftKey: "",
          aiMessage: { type: "success", text: "Saved key cleared." }
        });
      }
    } catch (err) {
      const msg = `${err.message}${err.requestId ? ` (RID: ${err.requestId})` : ""} `;
      this.setState({ aiLoading: false, aiMessage: { type: "error", text: msg } });
    }
  }

}
